import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "./richEditorApi";

/** Екранування тексту для вставки в HTML-атрибути/контент. */
function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function safeUrl(raw, { allowDataImage = false } = {}) {
  const u = String(raw || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (allowDataImage && /^data:image\//i.test(u)) return u;
  if (/^\/api\/lms\/media\//i.test(u)) return u;
  return "";
}

/** Довірені embed-плеєри (YouTube / Vimeo) — не вирізати з HTML. */
function isTrustedVideoEmbedSrc(src) {
  const s = String(src || "").trim();
  return (
    /^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com)\/embed\/[\w-]+/i.test(s)
    || /^https:\/\/player\.vimeo\.com\/video\/\d+/i.test(s)
  );
}

function scrubUntrustedIframes(root) {
  if (!root) return;
  root.querySelectorAll("iframe").forEach((el) => {
    if (!isTrustedVideoEmbedSrc(el.getAttribute("src"))) el.remove();
  });
}

const FONTS = ["Arial", "Georgia", "Times New Roman", "Courier New", "Verdana", "Tahoma", "Roboto"];
// Розмір шрифту в пікселях (як у Word: 6–72)
const SIZES_PX = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];
// Міжрядковий інтервал (line-height) і відступ між абзацами — для контролю «дірок» після paste з GPT/Word
const LINE_HEIGHTS = [
  { v: "1", label: "1.0" },
  { v: "1.15", label: "1.15" },
  { v: "1.35", label: "1.35" },
  { v: "1.55", label: "1.55" },
  { v: "1.8", label: "1.8" },
  { v: "2", label: "2.0" },
];
const PARA_SPACINGS = [
  { v: "0", label: "0" },
  { v: "0.15em", label: "компакт" },
  { v: "0.35em", label: "звич." },
  { v: "0.75em", label: "шир." },
  { v: "1.2em", label: "дуже шир." },
];
const BLOCK_RE = /^(P|DIV|LI|H[1-6]|BLOCKQUOTE|TD|TH)$/i;
// Дії AI-помічника редактора
const AI_ACTIONS_UI = [
  ["fix", "🔤 Виправити помилки"],
  ["clarify", "💬 Зробити зрозумілішим"],
  ["structure", "🧩 Структурувати"],
  ["headings", "🔖 Додати заголовки"],
  ["shorten", "✂️ Скоротити"],
  ["expand", "➕ Розширити"],
  ["steps", "🔢 Покрокова інструкція"],
];
const CALLOUTS = [
  ["ВАЖЛИВО", "#e7f6ec", "#2e9b57", "❗"],
  ["УВАГА", "#fff7e0", "#f6b73c", "⚠️"],
  ["НЕ РОБИТИ", "#fdecea", "#e53935", "⛔"],
  ["ПІДКАЗКА", "#e8f1fe", "#3b82f6", "💡"],
];

/** Telegram/Chrome інколи кладуть у буфер текст двічі підряд (A+A). */
function collapseDuplicatedPlain(text) {
  let t = String(text || "");
  if (t.length < 12) return t;
  // точний A+A
  if (t.length % 2 === 0) {
    const half = t.length / 2;
    if (t.slice(0, half) === t.slice(half)) return t.slice(0, half);
  }
  // A + пробіл/перенос + A
  const m = t.match(/^([\s\S]+?)(?:\s*)\1$/);
  if (m && m[1].replace(/\s+/g, "").length >= 8) return m[1];
  return t;
}

/** HTML → plain зі збереженням рядків (<br> і блокові теги → \n). */
function htmlToPlainText(html) {
  try {
    const d = new DOMParser().parseFromString(`<div id="__pt">${html}</div>`, "text/html");
    const root = d.getElementById("__pt") || d.body;
    return blockPlainDoc(root).replace(/\u00a0/g, " ");
  } catch {
    return "";
  }
}

/** Як blockPlainWithBreaks, але ще ставить \n між блоковими елементами (для порівняння / paste). */
function blockPlainDoc(root) {
  let s = "";
  const walk = (node) => {
    if (!node) return;
    if (node.nodeType === 3) {
      s += node.nodeValue || "";
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.nodeName;
    if (tag === "BR") {
      s += "\n";
      return;
    }
    if (tag === "SCRIPT" || tag === "STYLE") return;
    const block = /^(P|DIV|LI|H[1-6]|TR|BLOCKQUOTE|PRE|SECTION|ARTICLE)$/.test(tag);
    if (block && s.length && !/\n$/.test(s)) s += "\n";
    for (let c = node.firstChild; c; c = c.nextSibling) walk(c);
    if (block && !/\n$/.test(s)) s += "\n";
  };
  walk(root);
  return s;
}

function countNonEmptyLines(text) {
  return String(text || "").split(/\r?\n/).filter((l) => l.replace(/[\u00a0\s]+/g, "").length).length;
}

function countNumberedLines(text) {
  return (String(text || "").match(/^\s*\d{1,2}[.)]\s*\S/gm) || []).length;
}

function isVisuallyEmptyBlock(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.classList?.contains("imgwrap") || el.classList?.contains("videoblock")) return false;
  if (el.querySelector("img, video, iframe, table, .imgwrap, .videoblock")) return false;
  // є вкладені «справжні» блоки з текстом — не чіпаємо обгортку тут
  const nested = el.querySelector("p, div, ul, ol, li, h1, h2, h3, table");
  if (nested && (nested.textContent || "").replace(/[\u00a0\u200b\u200c\u200d\ufeff]/g, "").trim()) {
    // сам елемент може бути порожньою обгорткою навколо контенту — розгортаємо окремо
  }
  const t = (el.textContent || "")
    .replace(/[\u00a0\u200b\u200c\u200d\ufeff]/g, " ")
    .replace(/\s+/g, "")
    .trim();
  return !t;
}

/**
 * Знаходить індекси маркерів однотипної нумерації (1) 2) … або 1. 2. …).
 * Повертає масив індексів цифри маркера (крім першого / тих, що вже з нового рядка).
 */
function numberedListBreakOffsets(text) {
  const s = String(text || "");
  if (s.length < 6) return [];
  // також після літери/закритих лапок: MyChatBot.3) або ».2)
  const re = /(^|[\n\r]|[.!?…;:»"“”)]|\u00a0)(\s*)(\d{1,2})([.)])(\s*)(?=\S)/g;
  const hits = [];
  let m;
  while ((m = re.exec(s)) !== null) {
    const num = parseInt(m[3], 10);
    const delim = m[4];
    const idx = m.index + m[1].length + m[2].length;
    if (delim === "." && /^\d{1,2}\.\d{1,2}(\.\d{2,4})?/.test(s.slice(idx))) continue;
    hits.push({ index: idx, num, delim });
  }
  if (hits.length < 2) return [];

  const byDelim = { ")": [], ".": [] };
  hits.forEach((h) => { if (byDelim[h.delim]) byDelim[h.delim].push(h); });
  let group = null;
  for (const d of [")", "."]) {
    const g = byDelim[d];
    if (g.length < 2) continue;
    let seq = 0;
    for (let i = 1; i < g.length; i++) {
      if (g[i].num === g[i - 1].num + 1) seq++;
    }
    const startsLikeList = g[0].num === 1 || g[0].num <= 2;
    if ((seq >= 1 && startsLikeList) || seq >= 2) {
      if (!group || g.length > group.length) group = g;
    }
  }
  if (!group) return [];

  const breaks = [];
  for (let i = 0; i < group.length; i++) {
    const h = group[i];
    if (h.index <= 0) continue;
    const prev = s[h.index - 1];
    if (prev === "\n" || prev === "\r") continue;
    breaks.push(h.index);
  }
  return breaks;
}

function breakInlineNumberedLists(text) {
  const s = String(text || "");
  const breaks = numberedListBreakOffsets(s);
  if (!breaks.length) return s;
  let out = s;
  for (let i = breaks.length - 1; i >= 0; i--) {
    const idx = breaks[i];
    out = out.slice(0, idx) + "\n" + out.slice(idx);
  }
  return out;
}

/** Plain з урахуванням <br> як \n (textContent їх «з’їдає»). */
function blockPlainWithBreaks(root) {
  let s = "";
  const walk = (node) => {
    if (!node) return;
    if (node.nodeType === 3) {
      s += node.nodeValue || "";
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.nodeName;
    if (tag === "BR") {
      s += "\n";
      return;
    }
    if (tag === "SCRIPT" || tag === "STYLE") return;
    for (let c = node.firstChild; c; c = c.nextSibling) walk(c);
  };
  walk(root);
  return s;
}

/** Зміщення в blockPlainWithBreaks → точка в DOM (BR рахується як 1 символ). */
function domPointFromTextOffset(root, targetOffset) {
  let count = 0;
  let found = null;
  const walk = (node) => {
    if (found || !node) return;
    if (node.nodeType === 3) {
      const len = (node.nodeValue || "").length;
      if (count + len >= targetOffset) {
        found = { node, offset: targetOffset - count };
        return;
      }
      count += len;
      return;
    }
    if (node.nodeType !== 1) return;
    if (node.nodeName === "BR") {
      if (count === targetOffset) {
        found = { node, offset: 0, atBr: true };
        return;
      }
      count += 1;
      return;
    }
    if (node.nodeName === "SCRIPT" || node.nodeName === "STYLE") return;
    for (let c = node.firstChild; c; c = c.nextSibling) walk(c);
  };
  walk(root);
  return found;
}

/** Вставити <br> перед склеєними маркерами нумерації всередині одного блоку. */
function splitNumberedListInBlock(block) {
  if (!block) return;
  const doc = block.ownerDocument || document;
  const plain = blockPlainWithBreaks(block);
  const breaks = numberedListBreakOffsets(plain);
  if (!breaks.length) return;
  [...breaks].sort((a, b) => b - a).forEach((off) => {
    const pt = domPointFromTextOffset(block, off);
    if (!pt || pt.atBr) return;
    try {
      const r = doc.createRange();
      r.setStart(pt.node, pt.offset);
      r.collapse(true);
      const br = doc.createElement("br");
      r.insertNode(br);
      if (br.nextSibling?.nodeType === 3 && /^\s/.test(br.nextSibling.nodeValue || "")) {
        br.nextSibling.nodeValue = br.nextSibling.nodeValue.replace(/^\s+/, "");
      }
    } catch { /* ignore */ }
  });
}

/**
 * Склеєні/багаторядкові нумеровані пункти → окремі <p> (не лише <br>).
 * execCommand(insertHTML) інакше знову зліплює їх в один абзац.
 */
function explodeNumberedListsToParagraphs(root) {
  if (!root) return;
  const doc = root.ownerDocument || document;
  const blocks = [...root.querySelectorAll("p, div, li")];
  const leaves = blocks.filter((block) => {
    if (block === root) return false;
    if (block.classList?.contains("imgwrap") || block.classList?.contains("videoblock")) return false;
    if (block.classList?.contains("callout")) return false;
    if (block.closest("pre, code, .imgwrap, .videoblock, table")) return false;
    if (block.querySelector("p, div, li, ul, ol, table, .imgwrap, .videoblock")) return false;
    return true;
  });
  const targets = leaves.length ? leaves : (
    !root.querySelector("p, div, li, ul, ol, table") ? [root] : []
  );

  targets.forEach((block) => {
    let text = breakInlineNumberedLists(blockPlainWithBreaks(block));
    const lines = text.split(/\n/).map((l) => l.replace(/\u00a0/g, " ").trimEnd());
    const kept = lines.filter((l) => l.replace(/[\s\u200b]+/g, "").length);
    const numCount = kept.filter((l) => /^\d{1,2}[.)]\s*\S/.test(l.trim())).length;
    // вибухаємо лише якщо є нумерація (або склеєна була розбита на ≥2 рядки з номерами)
    if (numCount < 2 && numberedListBreakOffsets(blockPlainWithBreaks(block)).length < 1) return;
    if (kept.length < 2) return;

    if (block === root) {
      root.innerHTML = "";
      kept.forEach((line) => {
        const p = doc.createElement("p");
        p.textContent = line;
        root.appendChild(p);
      });
      return;
    }
    const parent = block.parentNode;
    if (!parent) return;
    const first = doc.createElement("p");
    first.textContent = kept[0];
    parent.replaceChild(first, block);
    let prev = first;
    for (let i = 1; i < kept.length; i++) {
      const p = doc.createElement("p");
      p.textContent = kept[i];
      prev.after(p);
      prev = p;
    }
  });
}

/**
 * Розбити склеєну нумерацію на рівні абзацу (через <b>MyChatBot</b>.3) теж працює).
 * Вставляє <br> перед маркерами, зберігаючи розмітку.
 * Важливо: якщо paste без <p>/<div> (лише текст+<br>+<b>) — обробляємо сам root.
 */
function splitNumberedListsInDom(root) {
  if (!root) return;
  const blocks = [...root.querySelectorAll("p, div, li, td, th, h1, h2, h3, h4, blockquote")];
  const leaves = blocks.filter((block) => {
    if (block.classList?.contains("imgwrap") || block.classList?.contains("videoblock")) return false;
    if (block.closest("pre, code, .imgwrap, .videoblock")) return false;
    if (block.querySelector("p, div, li, ul, ol, table")) return false;
    return true;
  });
  // буфер часто дає фрагмент без блокових тегів → querySelector нічого не знаходить
  if (!leaves.length) {
    splitNumberedListInBlock(root);
    return;
  }
  leaves.forEach(splitNumberedListInBlock);
  // якщо після листових усе ще склеєно на рівні root (рідкісний мікс) — ще раз по root
  if (numberedListBreakOffsets(blockPlainWithBreaks(root)).length) {
    // не чіпаємо root якщо в ньому є вкладені блоки — зміщення з’їдуть через межі <p>
    if (!root.querySelector("p, div, li, ul, ol, table")) {
      splitNumberedListInBlock(root);
    }
  }
}

function normalizeEditorDom(root) {
  if (!root) return;

  // Склеєні 1) 2) 3) → <br> (зберігає <b>); окремі <p> робить paste/setEditorHtml
  splitNumberedListsInDom(root);

  // Word: <li><p>текст</p></li> → <li>текст</li>
  root.querySelectorAll("li > p").forEach((p) => {
    const li = p.parentNode;
    if (!li) return;
    while (p.firstChild) li.insertBefore(p.firstChild, p);
    p.remove();
  });

  // Telegram/Chrome: порожній <div><br></div> / <p><br></p> між абзацами = «дірка» в 1 рядок
  // АЛЕ: порожній <p> одразу після фото/відео лишаємо — там стоїть курсор після вставки
  let guard = 0;
  while (guard++ < 30) {
    const empties = [...root.querySelectorAll("p, div")].filter((el) => {
      if (el === root) return false;
      if (el.classList?.contains("imgwrap") || el.classList?.contains("videoblock")) return false;
      if (el.closest(".imgwrap, .videoblock, table")) return false;
      if (!isVisuallyEmptyBlock(el)) return false;
      const prev = el.previousElementSibling;
      if (prev && (
        prev.classList?.contains("imgwrap")
        || prev.classList?.contains("videoblock")
        || prev.classList?.contains("callout")
        || (prev.tagName || "").toLowerCase() === "table"
      )) {
        return false; // caret-slot після медіа / callout / таблиці
      }
      return true;
    });
    if (!empties.length) break;
    empties.forEach((el) => el.remove());
  }

  // Подвійні <br> і <br> в кінці абзацу перед наступним блоком
  root.querySelectorAll("br").forEach((br) => {
    if (br.closest(".callout")) return; // у callout Shift+Enter = перенос, не чіпаємо
    let n = br.nextSibling;
    while (n && n.nodeType === 3 && !String(n.textContent || "").trim()) n = n.nextSibling;
    if (n && n.nodeName === "BR") n.remove();
  });
  root.querySelectorAll("p, div").forEach((el) => {
    if (el.classList?.contains("imgwrap") || el.classList?.contains("videoblock")) return;
    if (el.classList?.contains("callout")) return; // не зрізати <br> після тексту в callout
    while (el.lastChild) {
      const last = el.lastChild;
      if (last.nodeType === 3 && !String(last.textContent || "").replace(/[\u00a0\u200b]/g, "").trim()) {
        // не лишаємо порожній блок без жодного вузла
        if (el.childNodes.length === 1) break;
        el.removeChild(last);
        continue;
      }
      if (last.nodeName === "BR") {
        // один <br> у порожньому p лишаємо (каретка); trailing br після тексту — геть
        if (el.childNodes.length === 1) break;
        el.removeChild(last);
        continue;
      }
      break;
    }
  });

  // Обнулити відступи (inline перебиває CSS з Word/TG).
  // line-height не форсимо — лишаємо вибір користувача / inherit від .richedit
  const keepLh = (root.style && root.style.lineHeight) || "";
  root.querySelectorAll("p, div, li, h1, h2, h3, h4, blockquote").forEach((el) => {
    if (el.classList?.contains("imgwrap") || el.classList?.contains("videoblock")) return;
    if (el.classList?.contains("callout")) return;
    const tag = (el.tagName || "").toLowerCase();
    const isHeading = /^h[1-6]$/.test(tag);
    el.style.paddingTop = "0";
    el.style.paddingBottom = "0";
    el.style.minHeight = "0";
    // Заголовки — margin/size з CSS; не затирати inline margin:0
    if (isHeading) {
      el.style.margin = "";
      el.style.marginTop = "";
      el.style.marginBottom = "";
      el.style.fontSize = "";
      // line-height лишаємо, якщо юзер задав «Міжрядк.»; інакше — з CSS
      return;
    }
    el.style.marginTop = "0";
    // marginBottom не чіпаємо, якщо вже заданий тулбаром «Абзац»; інакше 0 після paste
    if (!el.style.marginBottom || /^(2|1\.5|1\.2|20|30|40)(em|px)?$/i.test(el.style.marginBottom)) {
      el.style.marginBottom = "0";
    }
    el.style.marginLeft = el.style.marginLeft || "";
    el.style.marginRight = el.style.marginRight || "";
    if (keepLh) el.style.lineHeight = keepLh;
    else if (el.style.lineHeight && /^(2|1\.8|1\.6|2\.5|3)/.test(el.style.lineHeight)) {
      el.style.lineHeight = "1.4";
    }
  });
}

/**
 * Санітизація HTML для редактора / AI / перегляду.
 * Прибирає script, iframe, on* handlers, javascript: URLs.
 * Лишає безпечні img/video/callout/таблиці.
 */
function sanitizeEditorHtml(html, { collapseDupPlain = false } = {}) {
  const doc = new DOMParser().parseFromString(`<div id="root">${html || ""}</div>`, "text/html");
  const root = doc.getElementById("root");
  if (!root) return "";

  root.querySelectorAll(
    "o\\:p, xml, script, style, meta, link, object, embed, form, input, button, textarea, select",
  ).forEach((el) => el.remove());
  scrubUntrustedIframes(root);

  root.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((a) => {
      const n = a.name.toLowerCase();
      const v = a.value || "";
      if (n.startsWith("on") || n === "srcdoc" || n === "formaction" || n === "xlink:href") {
        el.removeAttribute(a.name);
        return;
      }
      if ((n === "href" || n === "src") && /^\s*(javascript:|vbscript:|data:text\/html)/i.test(v)) {
        el.removeAttribute(a.name);
        return;
      }
      if (n === "src" && /^\s*data:/i.test(v) && !/^\s*data:image\//i.test(v)) {
        el.removeAttribute(a.name);
      }
      if (n === "class" && /mso|OutlineElement|Apple-|tg-/i.test(v)) {
        el.removeAttribute(a.name);
      }
    });
    if (el.className && typeof el.className === "string") {
      el.className = el.className.split(/\s+/).filter((c) => !/^(mso|Mso|Apple-|OutlineElement|tg-)/i.test(c)).join(" ");
      if (!el.className) el.removeAttribute("class");
    }
    const isCallout = el.classList?.contains?.("callout");
    const st = el.getAttribute("style");
    if (st) {
      const keep = [];
      st.split(";").forEach((part) => {
        const p = part.trim();
        if (!p) return;
        if (/expression|url\s*\(\s*javascript/i.test(p)) return;
        const key = p.split(":")[0].trim().toLowerCase();
        const textOk = /^(color|background-color|font-weight|font-style|text-decoration|text-align|font-size|font-family|line-height)$/.test(key);
        const layoutOk = /^(margin|margin-top|margin-bottom|padding|width|max-width|height|border|border-left|border-radius|vertical-align|word-break|white-space|table-layout|border-collapse)$/.test(key);
        const calloutOk = isCallout && /^(background|border-left|border-radius|padding|margin)$/.test(key);
        if (textOk || layoutOk || calloutOk) keep.push(p);
      });
      if (keep.length) el.setAttribute("style", keep.join("; "));
      else el.removeAttribute("style");
    }
    ["hspace", "vspace", "align"].forEach((a) => el.removeAttribute(a));
  });

  restoreCalloutStyles(root);
  try { normalizeEditorDom(root); } catch { /* ignore */ }

  root.querySelectorAll("table").forEach((t) => {
    t.style.tableLayout = "fixed";
    t.style.width = "100%";
    t.style.borderCollapse = "collapse";
    t.querySelectorAll("td, th").forEach((td) => {
      td.style.whiteSpace = "normal";
      td.style.wordBreak = "break-word";
      td.style.verticalAlign = "top";
      if (!td.style.border) td.style.border = "1px solid #ccc";
      if (!td.style.padding) td.style.padding = "6px";
    });
  });

  let out = root.innerHTML
    .replace(/(?:<br\s*\/?>\s*){2,}/gi, "<br>")
    .replace(/(<\/p>\s*)(<br\s*\/?>\s*)+/gi, "$1")
    .replace(/(<br\s*\/?>\s*)+(<p\b)/gi, "$2")
    .replace(/<div(?:\s[^>]*)?>\s*<br\s*\/?>\s*<\/div>/gi, "")
    .replace(/<p(?:\s[^>]*)?>\s*<br\s*\/?>\s*<\/p>/gi, "");

  if (/<img[\s>]|<video[\s>]/i.test(out)) return out;

  if (collapseDupPlain) {
    const plain = htmlToPlainText(out);
    const collapsed = collapseDuplicatedPlain(plain);
    if (collapsed !== plain && collapsed.length >= 8) {
      return plainTextToHtml(collapsed);
    }
  }
  return out;
}

/** Чистить HTML з буфера (Word/Docs/Telegram/таблиці). */
function sanitizePastedHtml(html) {
  return sanitizeEditorHtml(html, { collapseDupPlain: true });
}

export { sanitizeEditorHtml };

/** HTML виділення з редактора (включно з contenteditable=false imgwrap). */
function selectionClipboardHtml(root) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !root) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;
  const frag = range.cloneContents();
  const box = document.createElement("div");
  box.appendChild(frag);
  box.querySelectorAll(".richedit-drop-caret, .richedit-drag-ghost").forEach((el) => el.remove());
  box.querySelectorAll("img[src]").forEach((img) => {
    try {
      const raw = img.getAttribute("src") || "";
      if (!raw || raw.startsWith("data:") || raw.startsWith("blob:")) return;
      img.setAttribute("src", new URL(raw, window.location.href).href);
    } catch { /* leave as-is */ }
  });
  const html = box.innerHTML;
  return html && html.replace(/\s+/g, "").length ? html : null;
}

function selectionHasMedia(root) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || sel.isCollapsed || !root) return false;
  const range = sel.getRangeAt(0);
  try {
    for (const el of root.querySelectorAll("img, .imgwrap, .videoblock, video, iframe")) {
      if (range.intersectsNode(el)) return true;
    }
  } catch { /* ignore */ }
  return /<img[\s>]|<video[\s>]|<iframe[\s>]/i.test(selectionClipboardHtml(root) || "");
}

/** Чи є в HTML курсив/жирний/підкреслення тощо (не зрізати в plain). */
function htmlHasRichFormat(html) {
  const h = html || "";
  return /<(i|em|b|strong|u|s|strike|sub|sup|mark)\b/i.test(h)
    || /font-style\s*:\s*italic/i.test(h)
    || /font-weight\s*:\s*(bold|[6-9]00)/i.test(h)
    || /text-decoration\s*:\s*(underline|line-through)/i.test(h);
}

function htmlHasCallout(html) {
  return /\bcallout\b/i.test(html || "");
}

/** Відновити фон/бордер callout, якщо sanitize/буфер зрізали style. */
function restoreCalloutStyles(root) {
  if (!root?.querySelectorAll) return;
  root.querySelectorAll(".callout").forEach((el) => {
    const bg = (el.style && (el.style.backgroundColor || el.style.background)) || "";
    const hasBg = bg && !/^rgba?\(\s*0,\s*0,\s*0,\s*0\s*\)$/i.test(bg) && bg !== "transparent";
    if (hasBg && el.style.borderLeft) return;
    const head = (el.textContent || "").slice(0, 48);
    for (const [label, cBg, cBorder, icon] of CALLOUTS) {
      if (head.includes(label) || (icon && head.includes(icon))) {
        if (!hasBg) el.style.background = cBg;
        if (!el.style.borderLeft) el.style.borderLeft = `4px solid ${cBorder}`;
        if (!el.style.borderRadius) el.style.borderRadius = "8px";
        if (!el.style.padding) el.style.padding = "10px 12px";
        break;
      }
    }
  });
}

function plainTextToHtml(text) {
  // Кожен рядок → окремий <p>, щоб contentEditable не зліплював нумерацію в один блок
  const fixed = collapseDuplicatedPlain(breakInlineNumberedLists(String(text || "")))
    .replace(/\r\n/g, "\n").replace(/\r/g, "\n")
    .replace(/\n{3,}/g, "\n\n");
  return fixed.split("\n").map((line) => {
    const esc = line
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    if (!line.replace(/[\u00a0\s]+/g, "").length) return ""; // порожній рядок — пропускаємо (без «дірки»)
    return `<p>${esc}</p>`;
  }).filter(Boolean).join("");
}

/** Для вставки ВСЕРЕДИНУ callout: рядки через <br>, без <p> (інакше вилітає за блок). */
function plainTextToInlineBreaks(text) {
  return collapseDuplicatedPlain(breakInlineNumberedLists(String(text || "")))
    .replace(/\r\n/g, "\n").replace(/\r/g, "\n")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^\n+|\n+$/g, "")
    .replace(/\n/g, "<br>");
}

function isEditorBlock(el) {
  if (!el || el.nodeType !== 1 || !BLOCK_RE.test(el.tagName)) return false;
  if (el.classList?.contains("imgwrap") || el.classList?.contains("videoblock")) return false;
  return true;
}

/** Нормалізує computed line-height (px) → множник на кшталт "1.55". */
function ratioLineHeight(el) {
  try {
    const cs = getComputedStyle(el);
    const lh = cs.lineHeight;
    if (!lh || lh === "normal") return "1.55";
    if (lh.endsWith("px")) {
      const fs = parseFloat(cs.fontSize) || 16;
      const r = Math.round((parseFloat(lh) / fs) * 100) / 100;
      const known = LINE_HEIGHTS.map((x) => x.v);
      const hit = known.find((v) => Math.abs(parseFloat(v) - r) < 0.08);
      return hit || String(r);
    }
    return lh;
  } catch {
    return "";
  }
}

// Іконка вирівнювання у стилі Word (4 горизонтальні лінії)
function AlignIcon({ type }) {
  const ys = [3, 6.3, 9.6, 12.9];
  const shortByType = { left: [1, 10], center: [3.5, 12.5], right: [6, 15] };
  const short = shortByType[type] || [1, 15];
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" style={{ display: "block" }} aria-hidden>
      {ys.map((y, i) => {
        const [x1, x2] = (i % 2 === 1 && type !== "full") ? short : [1, 15];
        return <line key={i} x1={x1} y1={y} x2={x2} y2={y} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />;
      })}
    </svg>
  );
}

/** Шлях вузла від кореня редактора — переживає втрату фокусу (file dialog / paste). */
function nodeToPath(node, root) {
  const path = [];
  let n = node;
  while (n && n !== root) {
    const parent = n.parentNode;
    if (!parent) return null;
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, n));
    n = parent;
  }
  return n === root ? path : null;
}
function pathToNode(path, root) {
  let n = root;
  for (const i of path || []) {
    if (!n?.childNodes?.[i]) return null;
    n = n.childNodes[i];
  }
  return n;
}
function rangeToBookmark(range, root) {
  if (!range || !root) return null;
  const sp = nodeToPath(range.startContainer, root);
  const ep = nodeToPath(range.endContainer, root);
  if (!sp || !ep) return null;
  return {
    start: { path: sp, offset: range.startOffset },
    end: { path: ep, offset: range.endOffset },
  };
}
function bookmarkToRange(bm, root) {
  if (!bm || !root) return null;
  const sn = pathToNode(bm.start?.path, root);
  const en = pathToNode(bm.end?.path, root);
  if (!sn || !en) return null;
  const clamp = (node, off) => {
    if (node.nodeType === 3) return Math.max(0, Math.min(off, node.length));
    return Math.max(0, Math.min(off, node.childNodes.length));
  };
  try {
    const r = document.createRange();
    r.setStart(sn, clamp(sn, bm.start.offset));
    r.setEnd(en, clamp(en, bm.end.offset));
    return r;
  } catch {
    return null;
  }
}
function htmlToFragment(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const frag = document.createDocumentFragment();
  while (tmp.firstChild) frag.appendChild(tmp.firstChild);
  return frag;
}

export default function RichEditor({ value, onChange }) {
  const ref = useRef(null);
  const inited = useRef(false);
  const saved = useRef(null);
  const bookmark = useRef(null); // {start,end} — надійніше за Range після file picker
  const skipValueSync = useRef(false); // щоб onChange → value не збивав курсор
  const lastLocalHtml = useRef(null);  // останній HTML, який ми самі віддали в onChange
  const batchUpload = useRef(false);   // під час масового upload не даємо value перезаписати DOM
  const suppressInputSync = useRef(false); // під час безпечного UL/OL — не ганяти sync з onInput
  const videoFileRef = useRef(null);
  const fileRef = useRef(null);
  const imageFileRef = useRef(null);
  const aiBtnRef = useRef(null);
  const dragEl = useRef(null);
  const dropCaretRaf = useRef(0);
  /** Останній callout, де був курсор — для Ctrl+V, якщо Chrome «виносить» selection за блок. */
  const lastCalloutRef = useRef(null);
  const [st, setSt] = useState({});
  const [upload, setUpload] = useState(null);   // {name, pct} під час завантаження файлу
  const [aiMenu, setAiMenu] = useState(false);
  const [aiMenuPos, setAiMenuPos] = useState({ top: 0, left: 0 });
  const [ai, setAi] = useState(null);           // {scope:'sel'|'all', busy, result, error, action}

  // AI-помічник: працює з виділеним текстом, а якщо нічого не виділено — з усім матеріалом
  async function runAI(action) {
    setAiMenu(false);
    const sel = window.getSelection();
    const selText = sel && !sel.isCollapsed ? sel.toString().trim() : "";
    const scope = selText ? "sel" : "all";
    const text = selText || (ref.current?.innerText || "").trim();
    if (!text) { alert("Немає тексту для обробки."); return; }
    if (scope === "sel") saveSel();             // памʼятаємо виділення для заміни
    setAi({ scope, busy: true, result: "", action });
    try {
      const { html } = await api.aiRewrite(text, action);
      setAi((a) => a && { ...a, busy: false, result: sanitizeEditorHtml(html || "") });
    } catch (e) {
      setAi((a) => a && { ...a, busy: false, error: e.message || "AI недоступний" });
    }
  }
  function applyAI(mode) {
    const html = sanitizeEditorHtml(ai?.result || "");
    if (mode === "replace") {
      if (ai.scope === "sel") { restoreSel(); insertHTML(html); }
      else { if (ref.current) { ref.current.innerHTML = html; sync(); } }
    } else {                                     // вставити нижче/у курсор
      if (ai.scope === "sel") restoreSel();
      insertHTML(html);
    }
    setAi(null);
  }
  function openAiMenu(e) {
    e.preventDefault();
    e.stopPropagation();
    saveSel();
    const rect = (aiBtnRef.current || e.currentTarget)?.getBoundingClientRect?.();
    if (rect) {
      setAiMenuPos({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.right - 220, window.innerWidth - 228)),
      });
    }
    setAiMenu((v) => !v);
  }

  // Завантаження файлу на сервер з видимим прогресом
  async function doUpload(f) {
    setUpload({ name: f.name || "файл", pct: 0, size: f.size });
    try {
      const r = await api.uploadFileProgress(f, (pct) => setUpload((u) => u && { ...u, pct }));
      return r;
    } finally {
      setUpload(null);
    }
  }

  function setEditorHtml(html) {
    if (!ref.current) return;
    const isHtml = /<\/?[a-z][^>]*>/i.test(html || "");
    // Санітизуємо при завантаженні (XSS у збереженому body), img/http лишаються
    const safe = isHtml
      ? sanitizeEditorHtml(html || "")
      : String(html || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");
    ref.current.innerHTML = safe;
    wrapImages();
    try {
      splitNumberedListsInDom(ref.current);
      explodeNumberedListsToParagraphs(ref.current);
    } catch {}
  }

  function nodeInside(root, node) {
    if (!root || !node) return false;
    return root === node || root.contains(node);
  }

  /**
   * Єдине тіло callout: усе після мітки <b> у span.callout-body.
   * Інакше Chrome пише текст «між» міткою і span / за блок.
   */
  function ensureCalloutBody(callout) {
    if (!callout) return null;
    const label = [...callout.children].find((el) => (el.tagName || "").toLowerCase() === "b") || null;
    let body = callout.querySelector(":scope > span.callout-body");
    if (!body) {
      const firstSpan = [...callout.children].find((el) => (el.tagName || "").toLowerCase() === "span");
      body = firstSpan || document.createElement("span");
      body.classList.add("callout-body");
      if (!firstSpan) {
        if (label && label.nextSibling) callout.insertBefore(body, label.nextSibling);
        else callout.appendChild(body);
      }
    } else {
      body.classList.add("callout-body");
    }
    // Зібрати «хвості» після мітки в body (текстові вузли / зайві span)
    const move = [];
    let seenLabel = !label;
    for (const n of [...callout.childNodes]) {
      if (n === label) { seenLabel = true; continue; }
      if (!seenLabel) continue;
      if (n === body) continue;
      move.push(n);
    }
    move.forEach((n) => body.appendChild(n));
    // зайві вкладені p/div → br + вміст
    body.querySelectorAll("p, div, h1, h2, h3, h4").forEach((el) => {
      const kids = [...el.childNodes];
      const br = document.createElement("br");
      el.replaceWith(br, ...kids);
    });
    return body;
  }

  /** Plain → DocumentFragment лише з Text + <br> (без HTML, щоб не вилітати з callout). */
  function plainToCalloutFragment(text) {
    const frag = document.createDocumentFragment();
    const lines = String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    lines.forEach((line, i) => {
      if (i > 0) frag.appendChild(document.createElement("br"));
      if (line) frag.appendChild(document.createTextNode(line));
    });
    if (!frag.childNodes.length) frag.appendChild(document.createElement("br"));
    return frag;
  }

  /**
   * Ctrl+V у callout.
   * ВАЖЛИВО: не кликати restoreSel()/focus — Chrome тоді ставить caret ПІСЛЯ .callout.
   */
  function pasteIntoCallout(callout, plainText, capturedRange) {
    if (!callout || !ref.current) return;
    const body = ensureCalloutBody(callout);
    if (!body) return;
    skipValueSync.current = true;
    lastCalloutRef.current = callout;

    const text = String(plainText || "").replace(/\u200b/g, "");
    if (!text) return;

    try {
      let range = null;
      if (capturedRange) {
        try {
          const anc = capturedRange.commonAncestorContainer;
          if (nodeInside(body, anc)) range = capturedRange.cloneRange();
          else if (nodeInside(callout, anc)) {
            range = document.createRange();
            range.selectNodeContents(body);
            range.collapse(false);
          }
        } catch { range = null; }
      }
      if (!range) {
        range = document.createRange();
        const ph = (body.textContent || "").replace(/[\u200b\s]/g, "").trim();
        if (ph === "текст…" || ph === "текст..." || !ph) {
          range.selectNodeContents(body);
        } else {
          range.selectNodeContents(body);
          range.collapse(false);
        }
      }

      range.deleteContents();
      const frag = plainToCalloutFragment(text);
      const last = frag.lastChild;
      range.insertNode(frag);

      const sel = window.getSelection();
      const after = document.createRange();
      if (last && body.contains(last)) {
        if (last.nodeType === 3) after.setStart(last, last.nodeValue?.length || 0);
        else after.setStartAfter(last);
      } else {
        after.selectNodeContents(body);
      }
      after.collapse(false);
      sel.removeAllRanges();
      sel.addRange(after);
      saved.current = after.cloneRange();
      bookmark.current = rangeToBookmark(after, ref.current);
    } catch {
      try {
        body.appendChild(plainToCalloutFragment(text));
      } catch { /* ignore */ }
    }
    scrubCalloutChrome(callout);
  }

  /** Знайти callout для paste: live selection → bookmark → lastCallout. */
  function resolvePasteCallout() {
    const ed = ref.current;
    if (!ed) return { callout: null, range: null };
    const sel = window.getSelection();
    const pick = (node) => {
      if (!node) return null;
      const el = node.nodeType === 3 ? node.parentElement : node;
      const c = el?.closest?.(".callout") || null;
      return (c && ed.contains(c)) ? c : null;
    };
    let callout = pick(sel?.anchorNode) || pick(sel?.focusNode);
    let range = null;
    if (sel?.rangeCount) {
      try {
        range = sel.getRangeAt(0).cloneRange();
        if (!callout) callout = pick(range.commonAncestorContainer);
      } catch { /* ignore */ }
    }
    // Chrome: caret «після» callout, хоча користувач щойно був усередині
    if (!callout && lastCalloutRef.current && ed.contains(lastCalloutRef.current)) {
      const c = lastCalloutRef.current;
      try {
        if (sel?.rangeCount) {
          const r = sel.getRangeAt(0);
          if (r.collapsed) {
            const after = document.createRange();
            after.setStartAfter(c);
            after.setEnd(r.endContainer, r.endOffset);
            const between = after.toString().replace(/[\u00a0\u200b\s]/g, "");
            // порожньо між кінцем callout і caret → це «винесений» курсор
            if (!between) {
              callout = c;
              range = null; // вставимо в кінець body
            }
          }
        }
      } catch { /* ignore */ }
    }
    return { callout, range };
  }

  /** Вставка блокового HTML без execCommand (він зліплює <p>1)</p><p>2)</p> в один рядок). */
  function pasteBlocksHtml(html) {
    const ed = ref.current;
    if (!ed || !html) return;
    const edEmpty = !String(ed.textContent || "").replace(/[\u00a0\u200b\s]/g, "").trim();
    if (edEmpty) {
      ed.innerHTML = html;
    } else {
      restoreSel();
      const sel = window.getSelection();
      let block = null;
      let inCallout = null;
      if (sel?.anchorNode && ed.contains(sel.anchorNode)) {
        let n = sel.anchorNode;
        if (n.nodeType === 3) n = n.parentElement;
        inCallout = n?.closest?.(".callout") || null;
        if (inCallout) {
          pasteIntoCallout(inCallout, htmlToPlainText(html), null);
          hardenImgs(ed);
          markDraggable();
          return;
        }
        block = n?.closest?.("p, div, li, h1, h2, h3, h4, blockquote");
        if (block && (block === ed || !ed.contains(block))) block = null;
        if (block?.classList?.contains("imgwrap") || block?.classList?.contains("videoblock")) block = null;
        if (block?.classList?.contains("callout")) block = null;
      }
      const frag = htmlToFragment(html);
      const nodes = [...frag.childNodes];
      if (!nodes.length) return;
      if (block && /<p\b/i.test(html)) {
        // вставити абзаци ПІСЛЯ поточного блоку (не всередину <p>)
        let anchor = block;
        nodes.forEach((node) => {
          anchor.after(node);
          anchor = node;
        });
        // якщо поточний блок був порожній — прибрати
        if (isVisuallyEmptyBlock(block)) block.remove();
        try {
          const s = window.getSelection();
          const r = document.createRange();
          r.selectNodeContents(anchor);
          r.collapse(false);
          s.removeAllRanges();
          s.addRange(r);
          saveSel();
        } catch {}
      } else if (sel?.rangeCount && ed.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const last = frag.lastChild;
        range.insertNode(frag);
        if (last) {
          try {
            const after = document.createRange();
            after.setStartAfter(last);
            after.collapse(true);
            sel.removeAllRanges();
            sel.addRange(after);
            saveSel();
          } catch {}
        }
      } else {
        ed.appendChild(frag);
      }
    }
    hardenImgs(ed);
    markDraggable();
  }

  // Перший mount + зовнішні зміни value (load курсу, відновлення версії/чернетки)
  useEffect(() => {
    if (!ref.current) return;
    const next = value || "";
    if (!inited.current) {
      setEditorHtml(next);
      lastLocalHtml.current = next;
      inited.current = true;
      return;
    }
    // поки вантажимо кілька файлів — не чіпаємо DOM ззовні
    if (batchUpload.current) return;
    const dom = ref.current.innerHTML || "";
    if (next === dom) {
      skipValueSync.current = false;
      return;
    }
    // Поки ми самі пишемо в DOM (callout/paste) — НІКОЛИ не накатувати props,
    // навіть якщо value ще старий (інакше callout «не вставляється»).
    if (skipValueSync.current) {
      if (next === (lastLocalHtml.current || "")) skipValueSync.current = false;
      return;
    }
    // Справжнє зовнішнє оновлення (restore / load)
    setEditorHtml(next);
    lastLocalHtml.current = next;
  }, [value]);

  // formatBlock: "h3" | "Heading 3" | "<h3>" → "h3"
  function normBlockTag(v) {
    let b = String(v || "").toLowerCase().replace(/[<>]/g, "").trim();
    if (!b) return "";
    if (/^heading\s*(\d)$/.test(b)) return `h${b.replace(/\D/g, "")}`;
    if (/^h[1-6]$/.test(b) || b === "p" || b === "div" || b === "blockquote" || b === "pre") return b;
    return b;
  }
  function currentBlockTag() {
    try { return normBlockTag(document.queryCommandValue("formatBlock")); } catch { return ""; }
  }

  // Відстеження активного форматування виділення → підсвітка кнопок (як у Word)
  function refresh() {
    if (!ref.current) return;
    const q = (c) => { try { return document.queryCommandState(c); } catch { return false; } };
    let block = currentBlockTag();
    // fallback: піднятися по DOM до h1–h3 / blockquote
    if (!block || block === "div") {
      try {
        const sel = window.getSelection();
        let node = sel && sel.anchorNode;
        if (node && node.nodeType === 3) node = node.parentElement;
        while (node && node !== ref.current) {
          const tag = (node.tagName || "").toLowerCase();
          if (/^h[1-6]$/.test(tag) || tag === "blockquote") { block = tag; break; }
          node = node.parentElement;
        }
      } catch {}
    }
    let px = null;
    let lh = "";
    let para = "";
    try {
      const sel = window.getSelection();
      let node = sel && sel.anchorNode;
      if (node && node.nodeType === 3) node = node.parentElement;
      if (node && ref.current.contains(node)) {
        px = Math.round(parseFloat(getComputedStyle(node).fontSize));
        lh = ratioLineHeight(node);
        let blk = node;
        while (blk && blk !== ref.current && !isEditorBlock(blk)) blk = blk.parentElement;
        if (isEditorBlock(blk)) {
          const mb = (blk.style && blk.style.marginBottom) || "";
          para = mb || "";
        }
      }
    } catch {}
    // UL/OL: queryCommandState після власного toggle буває бреше — дивимось DOM
    let inUl = q("insertUnorderedList");
    let inOl = q("insertOrderedList");
    try {
      const sel = window.getSelection();
      let node = sel && sel.anchorNode;
      if (node && node.nodeType === 3) node = node.parentElement;
      const li = node?.closest?.("li");
      if (li && ref.current?.contains(li)) {
        const list = li.parentElement;
        inUl = !!(list && /^UL$/i.test(list.tagName));
        inOl = !!(list && /^OL$/i.test(list.tagName));
      }
    } catch {}
    setSt({ bold: q("bold"), italic: q("italic"), underline: q("underline"), strike: q("strikeThrough"),
            ul: inUl, ol: inOl,
            left: q("justifyLeft"), center: q("justifyCenter"), right: q("justifyRight"), full: q("justifyFull"),
            block, px, lh, para });
  }
  useEffect(() => {
    const h = () => {
      const sel = window.getSelection();
      if (ref.current && sel && sel.anchorNode && ref.current.contains(sel.anchorNode)) {
        // тримаємо bookmark актуальним — інакше H1/H2 після кліку в тулбар
        // відновлюють старий курсор (часто 1-й абзац)
        saveSel();
        refresh();
      }
    };
    document.addEventListener("selectionchange", h);
    return () => document.removeEventListener("selectionchange", h);
  }, []);

  // Памʼятаємо callout під курсором (для Ctrl+V після «вискоку» caret)
  useEffect(() => {
    const h = () => {
      try {
        const sel = window.getSelection();
        let n = sel?.anchorNode;
        if (n?.nodeType === 3) n = n.parentElement;
        const c = n?.closest?.(".callout");
        if (c && ref.current?.contains(c)) lastCalloutRef.current = c;
      } catch { /* ignore */ }
    };
    document.addEventListener("selectionchange", h);
    return () => document.removeEventListener("selectionchange", h);
  }, []);

  const sync = () => {
    scrubAllCallouts();
    const html = ref.current?.innerHTML || "";
    lastLocalHtml.current = html;
    skipValueSync.current = true;
    onChange(html);
    refresh();
    // Не скидати skip у microtask — React ще може віддати СТАРИЙ value і з’їсти callout.
    // Два rAF ≈ після commit нового value.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { skipValueSync.current = false; });
    });
  };
  /** onInput під час власного toggleList — ігноруємо (інакше шквал sync → OOM/краш). */
  const syncFromInput = () => {
    if (suppressInputSync.current) return;
    sync();
  };
  const saveSel = () => {
    const s = window.getSelection();
    if (s && s.rangeCount && ref.current?.contains(s.anchorNode)) {
      const r = s.getRangeAt(0).cloneRange();
      saved.current = r;
      bookmark.current = rangeToBookmark(r, ref.current);
    }
  };
  /** focus спочатку (інакше focus() збиває caret на початок), потім Range або bookmark. */
  const restoreSel = () => {
    if (!ref.current) return false;
    try { ref.current.focus({ preventScroll: true }); } catch { try { ref.current.focus(); } catch {} }
    let range = null;
    if (saved.current) {
      try {
        const s = window.getSelection();
        s.removeAllRanges();
        s.addRange(saved.current);
        range = saved.current;
      } catch {
        range = null;
      }
    }
    if (!range && bookmark.current) {
      range = bookmarkToRange(bookmark.current, ref.current);
      if (range) {
        try {
          const s = window.getSelection();
          s.removeAllRanges();
          s.addRange(range);
          saved.current = range.cloneRange();
        } catch {
          range = null;
        }
      }
    }
    return !!range;
  };
  const btn = (cmd, arg) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    saveSel();
    restoreSel();
    try { document.execCommand(cmd, false, arg); } catch { /* ignore */ }
    sync();
  };
  const applySel = (cmd, arg) => {
    restoreSel();
    try { document.execCommand(cmd, false, arg); } catch { /* ignore */ }
    sync();
  };
  function headingElAtCaret() {
    try {
      const sel = window.getSelection();
      let node = sel && sel.anchorNode;
      if (node && node.nodeType === 3) node = node.parentElement;
      while (node && node !== ref.current) {
        const t = (node.tagName || "").toLowerCase();
        if (/^h[1-6]$/.test(t) || t === "blockquote") return node;
        node = node.parentElement;
      }
    } catch {}
    return null;
  }
  /** Прибрати inline font-size, щоб CSS заголовка (26/20/16px) реально застосувався */
  function clearInlineFontSizes(el) {
    if (!el) return;
    try {
      el.style.fontSize = "";
      el.querySelectorAll("*").forEach((n) => {
        if (n.style && n.style.fontSize) n.style.fontSize = "";
      });
    } catch {}
  }
  function placeCaretIn(el) {
    if (!el) return;
    try {
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(r);
      saved.current = r.cloneRange();
      bookmark.current = rangeToBookmark(r, ref.current);
    } catch {}
  }
  function swapBlockTag(el, newTag) {
    if (!el || !el.parentNode) return null;
    const neu = document.createElement(newTag);
    while (el.firstChild) neu.appendChild(el.firstChild);
    const tag = (newTag || "").toLowerCase();
    const isHeading = /^h[1-6]$/.test(tag);
    neu.style.fontSize = "";
    neu.style.fontWeight = "";
    neu.style.paddingTop = "0";
    neu.style.paddingBottom = "0";
    if (!isHeading) {
      neu.style.marginTop = "0";
      neu.style.marginBottom = "0";
      const rootLh = (ref.current && ref.current.style.lineHeight) || "";
      if (rootLh) neu.style.lineHeight = rootLh;
    } else {
      neu.style.margin = "";
      neu.style.marginTop = "";
      neu.style.marginBottom = "";
      neu.style.lineHeight = "";
    }
    clearInlineFontSizes(neu);
    el.parentNode.replaceChild(neu, el);
    placeCaretIn(neu);
    return neu;
  }
  /** Заголовок із <li>: винести з ul/ol (інакше <li><h3> або нічого). */
  function headingFromListItem(li, want) {
    const list = li?.parentElement;
    if (!li || !list || !/^UL|OL$/i.test(list.tagName)) return null;
    const heading = document.createElement(want);
    while (li.firstChild) heading.appendChild(li.firstChild);
    clearInlineFontSizes(heading);
    heading.style.fontSize = "";
    heading.style.margin = "";
    if (list.children.length === 1) {
      list.parentNode.replaceChild(heading, list);
    } else {
      list.parentNode.insertBefore(heading, list);
      li.remove();
      if (!list.children.length) list.remove();
    }
    placeCaretIn(heading);
    return heading;
  }
  function liAtCaret() {
    try {
      const sel = window.getSelection();
      let node = sel && sel.anchorNode;
      if (node && node.nodeType === 3) node = node.parentElement;
      while (node && node !== ref.current) {
        if ((node.tagName || "").toLowerCase() === "li") return node;
        node = node.parentElement;
      }
    } catch {}
    return null;
  }
  /** Блок під курсором (p/div/heading/blockquote) — не плутати з обгорткою всього редактора. */
  function blockAtCaret() {
    try {
      const sel = window.getSelection();
      let node = sel && sel.anchorNode;
      if (node && node.nodeType === 3) node = node.parentElement;
      while (node && node !== ref.current) {
        const t = (node.tagName || "").toLowerCase();
        if (t === "p" || /^h[1-6]$/.test(t) || t === "blockquote") return node;
        // div — лише «листовий» блок, не обгортка з кількома абзацами
        if (t === "div" && !node.classList?.contains("imgwrap")
            && !node.classList?.contains("videoblock")
            && !node.classList?.contains("callout")
            && !node.querySelector("p, h1, h2, h3, h4, ul, ol, div")) {
          return node;
        }
        node = node.parentElement;
      }
    } catch {}
    return null;
  }
  /** H1/H2/H3/❝: повторний клік по активному (оранжевому) → plain <p> */
  const applyHeading = (tag) => (e) => {
    e.preventDefault(); // не забирати focus з редактора
    // Актуальний курсор: спочатку live selection, і лише якщо втрачено — bookmark
    try {
      const s = window.getSelection();
      if (s?.anchorNode && ref.current?.contains(s.anchorNode)) {
        saveSel();
      } else {
        restoreSel();
      }
    } catch {
      restoreSel();
    }
    const want = normBlockTag(tag);
    const el = headingElAtCaret();
    const li = liAtCaret();
    const cur = normBlockTag(el?.tagName) || currentBlockTag();
    const demote = cur === want;
    if (demote) {
      if (el) swapBlockTag(el, "p");
      else {
        try { document.execCommand("formatBlock", false, "p"); } catch {}
        try { if (currentBlockTag() === want) document.execCommand("formatBlock", false, "<p>"); } catch {}
      }
      const plain = headingElAtCaret() ? null : (() => {
        try {
          const sel = window.getSelection();
          let node = sel && sel.anchorNode;
          if (node && node.nodeType === 3) node = node.parentElement;
          while (node && node !== ref.current) {
            if ((node.tagName || "").toLowerCase() === "p") return node;
            node = node.parentElement;
          }
        } catch {}
        return null;
      })();
      if (plain) {
        clearInlineFontSizes(plain);
        try {
          const prev = plain.previousElementSibling;
          const next = plain.nextElementSibling;
          [prev, next].forEach((sib) => {
            if (sib && isVisuallyEmptyBlock(sib) && !sib.classList?.contains("imgwrap")
                && !sib.classList?.contains("videoblock")) sib.remove();
          });
        } catch {}
      }
    } else if (li && !el) {
      headingFromListItem(li, want);
    } else {
      let target = el || blockAtCaret();
      if (target) {
        // заголовок усередині li → винести
        if (li && li.contains(target) && target !== li) {
          while (li.firstChild) li.removeChild(li.firstChild);
          li.appendChild(target);
          headingFromListItem(li, want);
        } else {
          swapBlockTag(target, want);
        }
      } else if (li) {
        headingFromListItem(li, want);
      } else {
        // останній шанс — formatBlock по поточному виділенню (без стрибка на 1-й абзац)
        const next = String(tag || "").toUpperCase().replace(/[<>]/g, "");
        try { document.execCommand("formatBlock", false, next); } catch {}
        try {
          if (currentBlockTag() !== want) document.execCommand("formatBlock", false, `<${want}>`);
        } catch {}
        const after = headingElAtCaret() || blockAtCaret();
        if (after && normBlockTag(after.tagName) === want) clearInlineFontSizes(after);
        else if (after && !/^h[1-6]$/.test(normBlockTag(after.tagName))) swapBlockTag(after, want);
      }
    }
    if (ref.current) {
      try {
        // bookmark ПІСЛЯ зміни блоку (placeCaretIn), не застарілий з початку кліку
        const bm = bookmark.current;
        normalizeEditorDom(ref.current);
        const keep = [...ref.current.querySelectorAll("h1,h2,h3,h4")];
        keep.forEach((h) => {
          if (!h.isConnected) return;
          h.style.margin = "";
          h.style.marginTop = "";
          h.style.marginBottom = "";
          h.style.fontSize = "";
        });
        if (bm) {
          const r = bookmarkToRange(bm, ref.current);
          if (r) {
            const s = window.getSelection();
            s.removeAllRanges();
            s.addRange(r);
            saved.current = r.cloneRange();
            bookmark.current = rangeToBookmark(r, ref.current);
          }
        }
      } catch {}
    }
    sync();
  };
  /** Блок верхнього рівня з вузла (прямий нащадок .richedit). */
  function topLevelFromNode(node) {
    const ed = ref.current;
    if (!ed || !node || !ed.contains(node)) return null;
    let n = node;
    if (n.nodeType === 3) n = n.parentElement;
    if (!n) return null;
    const special = n.closest?.(".callout, table, .imgwrap, .videoblock");
    if (special && ed.contains(special)) return special;
    let el = n;
    while (el && el.parentElement && el.parentElement !== ed) el = el.parentElement;
    if (el && el.parentElement === ed && el !== ed) return el;
    return null;
  }

  /** Курсор у кінці редактора (останній блок / після всього тексту). */
  function isCaretAtEditorEnd() {
    const ed = ref.current;
    if (!ed) return false;
    try {
      const sel = window.getSelection();
      if (!sel?.rangeCount || !ed.contains(sel.anchorNode)) return false;
      const r = sel.getRangeAt(0);
      if (!r.collapsed) return false;
      const probe = document.createRange();
      probe.selectNodeContents(ed);
      probe.setStart(r.endContainer, r.endOffset);
      return !(probe.toString() || "").replace(/[\u00a0\u200b\s]/g, "");
    } catch {
      return false;
    }
  }

  /**
   * Вставка блокового HTML (table тощо) через DOM — не execCommand.
   * Якір: живий курсор → bookmark → після поточного блоку.
   * Не кидаємо блок у кінець документа лише тому, що caret у останньому абзаці.
   */
  function insertBlockAtCaret(html, markerSel) {
    const ed = ref.current;
    if (!ed || !html) return null;
    try {
      try { ed.focus({ preventScroll: true }); } catch { try { ed.focus(); } catch {} }
      // Відновити caret, якщо кнопка тулбару збила виділення
      if (!(window.getSelection()?.anchorNode && ed.contains(window.getSelection().anchorNode))) {
        restoreSel();
      }

      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      const nodes = [...tmp.childNodes];
      if (!nodes.length) return null;

      const sel = window.getSelection();
      const liveInEditor = !!(sel?.anchorNode && ed.contains(sel.anchorNode));
      let anchor = liveInEditor ? topLevelFromNode(sel.anchorNode) : null;
      if (!anchor) {
        const bmRange = bookmarkToRange(bookmark.current, ed) || saved.current;
        if (bmRange) {
          try {
            const n = bmRange.startContainer;
            anchor = topLevelFromNode(n);
          } catch {}
        }
      }

      if (!anchor || !ed.contains(anchor)) {
        // Немає якоря — лише тоді в кінець (прибрати порожні хвости)
        while (ed.lastElementChild && isVisuallyEmptyBlock(ed.lastElementChild)
          && !ed.lastElementChild.classList?.contains("callout")
          && !ed.lastElementChild.classList?.contains("imgwrap")
          && !ed.lastElementChild.classList?.contains("videoblock")
          && (ed.lastElementChild.tagName || "").toLowerCase() !== "table") {
          ed.lastElementChild.remove();
        }
        nodes.forEach((node) => ed.appendChild(node));
      } else if (isVisuallyEmptyBlock(anchor)
        && !anchor.classList?.contains("callout")
        && !anchor.classList?.contains("imgwrap")
        && !anchor.classList?.contains("videoblock")
        && (anchor.tagName || "").toLowerCase() !== "table") {
        // Порожній абзац під курсором — замінити ним
        let prev = null;
        nodes.forEach((node, i) => {
          if (i === 0) {
            anchor.replaceWith(node);
            prev = node;
          } else {
            prev.after(node);
            prev = node;
          }
        });
      } else {
        let prev = anchor;
        nodes.forEach((node) => {
          prev.after(node);
          prev = node;
        });
      }

      hardenImgs(ed);
      markDraggable();
      // skip одразу, ДО normalize/sync — щоб effect не з’їв блок
      skipValueSync.current = true;
      normalizeEditorDom(ed);
      const marker = markerSel ? ed.querySelector(markerSel) : null;
      const wrap = marker?.closest?.(".imgwrap") || marker?.closest?.(".videoblock") || marker;
      const target = wrap && wrap.isConnected ? wrap : null;
      if (target?.classList?.contains("imgwrap")) {
        prepMediaWrap(target);
        clampImgWrap(target);
        ensureCaretAfterMedia(target);
      } else if (target?.classList?.contains("videoblock")) {
        prepMediaWrap(target);
        ensureCaretAfterMedia(target);
      } else if (target?.classList?.contains("callout")) {
        ensureCaretInCallout(target);
      } else if (target) {
        ensureCaretAfterCallout(target);
      }
      try { target?.scrollIntoView?.({ block: "nearest", behavior: "smooth" }); } catch {}
      sync();
      return target;
    } catch (err) {
      console.error("insertBlockAtCaret", err);
      try { alert("Не вдалося вставити блок: " + (err?.message || err)); } catch {}
      return null;
    }
  }

  const insertHTML = (h, markerSel) => {
    if (!ref.current) return;
    // ВАЖЛИВО: не focus() після restoreSel — Chrome скидає курсор на початок редактора
    restoreSel();
    const before = ref.current.innerHTML?.length || 0;
    // рахуємо маркери ДО вставки — інакше старий .callout ховає невдалу вставку
    const markersBefore = markerSel ? ref.current.querySelectorAll(markerSel).length : 0;
    try { document.execCommand("insertHTML", false, h); } catch {}
    // Після file dialog / paste виділення часто мертве: execCommand «успішний», але в DOM нічого немає
    const markersAfter = markerSel ? ref.current.querySelectorAll(markerSel).length : 0;
    const missing = markerSel
      ? markersAfter <= markersBefore
      : (ref.current.innerHTML?.length || 0) <= before;
    if (missing) {
      // блокові вставки (callout / video) — через DOM; execCommand часто з'їдає iframe/video
      if (/callout|table|videoblock/i.test(h)) {
        insertBlockAtCaret(h, markerSel);
        return;
      }
      let placed = false;
      let range = null;
      const inEditor = (r) => {
        if (!r || !ref.current) return false;
        try { return ref.current.contains(r.commonAncestorContainer); } catch { return false; }
      };
      try {
        const s = window.getSelection();
        if (s?.rangeCount) range = s.getRangeAt(0);
      } catch {}
      if (!inEditor(range)) {
        range = bookmarkToRange(bookmark.current, ref.current) || saved.current;
      }
      if (inEditor(range)) {
        try {
          range.deleteContents();
          const frag = htmlToFragment(h);
          const last = frag.lastChild;
          range.insertNode(frag);
          if (last) {
            const after = document.createRange();
            after.setStartAfter(last);
            after.collapse(true);
            const s = window.getSelection();
            s.removeAllRanges();
            s.addRange(after);
            saved.current = after.cloneRange();
            bookmark.current = rangeToBookmark(after, ref.current);
          }
          placed = true;
        } catch { /* fall through */ }
      }
      // краще в кінець, ніж на початок
      if (!placed) ref.current.insertAdjacentHTML("beforeend", h);
    }
    hardenImgs(ref.current);
    markDraggable();
    // Курсор після вставленого медіа + скрол до нього (без стрибка наверх)
    if (markerSel) {
      // беремо ОСТАННІЙ маркер — щойно вставлений
      const list = ref.current.querySelectorAll(markerSel);
      const marker = list[list.length - 1] || null;
      const wrap = marker?.closest?.(".imgwrap") || marker?.closest?.(".videoblock") || marker;
      if (wrap?.classList?.contains("imgwrap")) {
        prepMediaWrap(wrap);
        clampImgWrap(wrap);
        ensureCaretAfterMedia(wrap);
        // без scrollIntoView — він дає «проскакування» редактора до фото
      } else if (wrap?.classList?.contains("callout")) {
        ensureCaretInCallout(wrap);
      } else if (wrap) {
        try {
          const after = document.createRange();
          after.setStartAfter(wrap.nextSibling?.nodeName === "P" ? wrap.nextSibling : wrap);
          after.collapse(true);
          const s = window.getSelection();
          s.removeAllRanges();
          s.addRange(after);
          saved.current = after.cloneRange();
          bookmark.current = rangeToBookmark(after, ref.current);
        } catch {}
      }
    }
    // Chrome після insertHTML лишає порожні <p><br></p> — прибираємо, але caret після фото лишаємо
    const bmBeforeNorm = bookmark.current;
    normalizeEditorDom(ref.current);
    try {
      const s = window.getSelection();
      if (markerSel) {
        const list = ref.current.querySelectorAll(markerSel);
        const last = list[list.length - 1];
        if (last?.classList?.contains("callout") && last.isConnected) {
          ensureCaretInCallout(last);
        } else if (s?.rangeCount && ref.current.contains(s.anchorNode)) {
          saved.current = s.getRangeAt(0).cloneRange();
          bookmark.current = rangeToBookmark(saved.current, ref.current);
        } else if (bmBeforeNorm) {
          const r = bookmarkToRange(bmBeforeNorm, ref.current);
          if (r) {
            s.removeAllRanges();
            s.addRange(r);
            saved.current = r.cloneRange();
            bookmark.current = rangeToBookmark(r, ref.current);
          }
        }
      } else if (s?.rangeCount && ref.current.contains(s.anchorNode)) {
        saved.current = s.getRangeAt(0).cloneRange();
        bookmark.current = rangeToBookmark(saved.current, ref.current);
      } else if (bmBeforeNorm) {
        const r = bookmarkToRange(bmBeforeNorm, ref.current);
        if (r) {
          s.removeAllRanges();
          s.addRange(r);
          saved.current = r.cloneRange();
          bookmark.current = rangeToBookmark(r, ref.current);
        }
      }
    } catch {}
    sync();
  };

  // Розмір у пікселях: execCommand дає лише 1-7, тому підміняємо на span із font-size:Npx
  function applyFontSizePx(px) {
    restoreSel(); // вже робить focus({preventScroll})
    document.execCommand("fontSize", false, "7");
    ref.current?.querySelectorAll('font[size="7"]').forEach((f) => {
      const span = document.createElement("span");
      span.style.fontSize = px + "px";
      while (f.firstChild) span.appendChild(f.firstChild);
      f.replaceWith(span);
    });
    sync();
  }

  /** Блоки в поточному виділенні (або блок під курсором). */
  function getSelectedBlocks() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !ref.current) return [];
    const range = sel.getRangeAt(0);
    const out = [];
    const pushBlock = (node) => {
      let el = node;
      if (el && el.nodeType === 3) el = el.parentElement;
      while (el && el !== ref.current) {
        if (isEditorBlock(el)) {
          if (!out.includes(el)) out.push(el);
          return;
        }
        el = el.parentElement;
      }
    };
    pushBlock(range.startContainer);
    if (!range.collapsed) {
      try {
        const root = range.commonAncestorContainer;
        const scope = root.nodeType === 1 ? root : root.parentElement;
        if (scope && ref.current.contains(scope)) {
          scope.querySelectorAll("p,div,li,h1,h2,h3,h4,blockquote,td,th").forEach((el) => {
            if (!isEditorBlock(el) || !ref.current.contains(el)) return;
            try {
              if (range.intersectsNode(el) && !out.includes(el)) out.push(el);
            } catch { /* ignore */ }
          });
        }
      } catch { /* ignore */ }
    }
    return out;
  }

  /**
   * Безпечний UL/OL замість document.execCommand('insertUnorderedList').
   * Нативний execCommand біля .callout крашить вкладку Chrome («От халепа!»).
   * — поза коллаутом: DOM wrap/unwrap <p>/<li>;
   * — у коллауті: лише префікс «• » / «1. » (без <ul> всередині);
   * — .callout / media / table ніколи не кладемо в <li>.
   */
  function toggleList(ordered) {
    const ed = ref.current;
    if (!ed) return;
    suppressInputSync.current = true;
    skipValueSync.current = true;
    saveSel();
    restoreSel();

    const listTag = ordered ? "OL" : "UL";
    const isWantedList = (el) => el && (ordered ? /^OL$/i.test(el.tagName) : /^UL$/i.test(el.tagName));
    const inForbidden = (el) => !!(el && el.closest?.(".callout, table, .imgwrap, .videoblock"));

    const nearestListable = (node) => {
      let el = node;
      if (el && el.nodeType === 3) el = el.parentElement;
      while (el && el !== ed) {
        if (el.classList?.contains("callout")) return { kind: "callout", el };
        if (el.classList?.contains("imgwrap") || el.classList?.contains("videoblock")) return null;
        if (/^TABLE|TD|TH$/i.test(el.tagName)) return null;
        if (/^LI$/i.test(el.tagName)) return { kind: "li", el };
        if (/^(P|H1|H2|H3|H4|BLOCKQUOTE)$/i.test(el.tagName)) return { kind: "block", el };
        el = el.parentElement;
      }
      return null;
    };

    const prefixInCallout = () => {
      const sel = window.getSelection();
      if (!sel?.rangeCount) return;
      try {
        if (sel.isCollapsed) {
          document.execCommand("insertText", false, ordered ? "1. " : "• ");
        } else {
          const raw = sel.toString();
          const lines = raw.split(/\r?\n/);
          let n = 0;
          const out = lines.map((line) => {
            const t = line.replace(/\u00a0/g, " ");
            if (!t.trim()) return line;
            if (/^\s*[•\-\*]\s+/.test(t) || /^\s*\d+[.)]\s+/.test(t)) return line;
            n += 1;
            return ordered ? `${n}. ${t.replace(/^\s+/, "")}` : `• ${t.replace(/^\s+/, "")}`;
          }).join("\n");
          document.execCommand("insertText", false, out);
        }
      } catch { /* ignore */ }
    };

    /** Unwrap: розрізати ul на «до» + p + «після» при потребі. */
    const unwrapLis = (lis) => {
      // Групуємо по батьківському списку, обробляємо з кінця щоб індекси жили
      const byList = new Map();
      lis.forEach((li) => {
        const list = li.parentElement;
        if (!list || !/^UL|OL$/i.test(list.tagName)) return;
        if (!byList.has(list)) byList.set(list, []);
        byList.get(list).push(li);
      });
      byList.forEach((items, list) => {
        const all = [...list.children];
        // Якщо знімаємо всі li — замінити весь список на послідовність <p>
        if (items.length === all.length) {
          const ps = all.map((li) => {
            const p = document.createElement("p");
            while (li.firstChild) p.appendChild(li.firstChild);
            if (!p.childNodes.length) p.innerHTML = "<br>";
            return p;
          });
          let last = null;
          ps.forEach((p, i) => {
            if (i === 0) { list.replaceWith(p); last = p; }
            else { last.after(p); last = p; }
          });
          return;
        }
        // Частково: кожен li → p на місці (розрізає список)
        items.slice().reverse().forEach((li) => {
          const idx = [...list.children].indexOf(li);
          if (idx < 0) return;
          const p = document.createElement("p");
          while (li.firstChild) p.appendChild(li.firstChild);
          if (!p.childNodes.length) p.innerHTML = "<br>";
          const beforeLis = [...list.children].slice(0, idx);
          const afterLis = [...list.children].slice(idx + 1);
          if (!beforeLis.length && !afterLis.length) {
            list.replaceWith(p);
            return;
          }
          if (!beforeLis.length) {
            list.parentNode.insertBefore(p, list);
            li.remove();
            return;
          }
          if (!afterLis.length) {
            li.remove();
            list.after(p);
            return;
          }
          const listAfter = document.createElement(list.tagName);
          afterLis.forEach((x) => listAfter.appendChild(x));
          li.remove();
          list.after(p);
          p.after(listAfter);
        });
      });
    };

    try {
      const sel = window.getSelection();
      if (!sel?.rangeCount || !ed.contains(sel.anchorNode)) {
        return;
      }

      const hit = nearestListable(sel.anchorNode);
      if (hit?.kind === "callout") {
        prefixInCallout();
        return;
      }

      // Кандидати: виділені блоки, без callout/media/table
      let raw = getSelectedBlocks();
      if (!raw.length && hit?.el) raw = [hit.el];

      const targets = [];
      raw.forEach((b) => {
        if (!b || !ed.contains(b)) return;
        if (b.classList?.contains("callout")) return;
        if (inForbidden(b) && !/^LI$/i.test(b.tagName)) return;
        if (!/^(P|H1|H2|H3|H4|LI|BLOCKQUOTE)$/i.test(b.tagName)) return;
        // не тягнути вкладені p всередині li двічі
        if (/^P$/i.test(b.tagName) && b.closest("li") && b.parentElement !== ed) {
          const li = b.closest("li");
          if (li && !targets.includes(li)) targets.push(li);
          return;
        }
        if (!targets.includes(b)) targets.push(b);
      });

      // document order
      targets.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

      if (!targets.length) {
        // порожнє місце — новий список з одного li
        const list = document.createElement(listTag.toLowerCase());
        const li = document.createElement("li");
        li.innerHTML = "<br>";
        list.appendChild(li);
        const anchor = topLevelFromNode(sel.anchorNode);
        if (anchor && ed.contains(anchor) && isVisuallyEmptyBlock(anchor)
            && !anchor.classList?.contains("callout")) {
          anchor.replaceWith(list);
        } else if (anchor && ed.contains(anchor) && !inForbidden(anchor)) {
          anchor.after(list);
        } else {
          ed.appendChild(list);
        }
        placeCaretIn(li);
        return;
      }

      const allLi = targets.every((t) => /^LI$/i.test(t.tagName));
      if (allLi) {
        const parents = [...new Set(targets.map((t) => t.parentElement).filter(Boolean))];
        const allWanted = parents.every(isWantedList);
        if (allWanted) {
          unwrapLis(targets);
          return;
        }
        // Змінити тип списку UL↔OL
        parents.forEach((list) => {
          if (!/^UL|OL$/i.test(list.tagName)) return;
          if (isWantedList(list)) return;
          const neu = document.createElement(listTag.toLowerCase());
          while (list.firstChild) neu.appendChild(list.firstChild);
          list.replaceWith(neu);
        });
        return;
      }

      // Абзаци/заголовки → список (callout не чіпаємо — вже відфільтровані)
      let lastList = null;
      let lastLi = null;
      targets.forEach((block) => {
        if (/^LI$/i.test(block.tagName)) {
          lastList = block.parentElement;
          lastLi = block;
          return;
        }
        const li = document.createElement("li");
        while (block.firstChild) li.appendChild(block.firstChild);
        if (!li.childNodes.length) li.innerHTML = "<br>";

        const prev = block.previousElementSibling;
        if (lastList && lastList.isConnected && isWantedList(lastList) && prev === lastList) {
          lastList.appendChild(li);
          block.remove();
        } else if (prev && isWantedList(prev)) {
          prev.appendChild(li);
          block.remove();
          lastList = prev;
        } else {
          const list = document.createElement(listTag.toLowerCase());
          list.appendChild(li);
          block.replaceWith(list);
          lastList = list;
        }
        lastLi = li;
      });
      if (lastLi) placeCaretIn(lastLi);
    } catch (err) {
      console.error("toggleList", err);
    } finally {
      suppressInputSync.current = false;
      try { hardenImgs(ed); markDraggable(); } catch {}
      sync();
    }
  }

  function allContentBlocks() {
    if (!ref.current) return [];
    return [...ref.current.querySelectorAll("p,div,li,h1,h2,h3,h4,blockquote")].filter(isEditorBlock);
  }

  /** Курсор без виділення → увесь текст; інакше лише виділені абзаци. */
  function spacingTargets() {
    const sel = window.getSelection();
    const collapsed = !sel || !sel.rangeCount || sel.isCollapsed;
    if (collapsed) return allContentBlocks();
    const blocks = getSelectedBlocks();
    return blocks.length ? blocks : allContentBlocks();
  }

  function applyLineHeight(lh) {
    restoreSel();
    const targets = spacingTargets();
    if (!targets.length && ref.current) {
      ref.current.style.lineHeight = lh;
    } else {
      targets.forEach((b) => {
        b.style.lineHeight = lh;
        // лише line-height — без штучного marginBottom (давав «щілини» між блоками)
        const tag = (b.tagName || "").toLowerCase();
        if (!/^h[1-6]$/.test(tag) && !b.classList?.contains("callout")) {
          b.style.marginTop = "0";
        }
        b.style.paddingTop = "0";
        b.style.paddingBottom = "0";
      });
      if (ref.current) ref.current.style.lineHeight = lh;
    }
    sync();
  }

  function applyParaSpacing(mb) {
    restoreSel();
    const targets = spacingTargets();
    targets.forEach((b) => {
      if (b.classList?.contains("callout")) return;
      const tag = (b.tagName || "").toLowerCase();
      if (/^h[1-6]$/.test(tag)) return; // відступи заголовків — з CSS
      b.style.marginTop = "0";
      b.style.marginBottom = mb;
      if (b.style.paddingTop) b.style.paddingTop = "0";
      if (b.style.paddingBottom) b.style.paddingBottom = "0";
    });
    sync();
  }

  /** Ущільнити відступи в редакторі (після paste або кнопка «⇅ ущільн.»). */
  function compactEditorSpacing(all = true) {
    const lh = (ref.current && ref.current.style.lineHeight) || "1.4";
    if (ref.current) {
      ref.current.style.lineHeight = lh;
      normalizeEditorDom(ref.current);
    }
    const targets = all ? allContentBlocks() : spacingTargets();
    targets.forEach((b) => {
      const tag = (b.tagName || "").toLowerCase();
      const isHeading = /^h[1-6]$/.test(tag);
      if (b.classList?.contains("callout")) return;
      b.style.paddingTop = "0";
      b.style.paddingBottom = "0";
      b.style.minHeight = "0";
      if (isHeading) {
        b.style.margin = "";
        b.style.marginTop = "";
        b.style.marginBottom = "";
        b.style.fontSize = "";
        // міжрядковий з тулбара — ок; інакше CSS
        if (lh) b.style.lineHeight = lh;
        return;
      }
      b.style.lineHeight = lh;
      b.style.marginTop = "0";
      b.style.marginBottom = "0";
    });
    sync();
  }
  function compactSpacing(e) {
    if (e) e.preventDefault();
    restoreSel();
    compactEditorSpacing(false);
  }

  // Абсолютне встановлення кольору.
  // Раніше чистили вкладені style.color="" → <a> знову брали CSS #e8402a і «колір не мінявся».
  // Тепер форсимо новий колір на всі вкладені вузли (включно з <a>/<font>).
  function applyColor(prop, value) {
    restoreSel();
    let sel = window.getSelection();
    if ((!sel || !sel.rangeCount) && saved.current) {
      try {
        sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(saved.current);
      } catch {}
    }
    if (!sel || !sel.rangeCount) {
      alert("Спочатку виділіть текст, якому змінити колір.");
      return;
    }
    const range = sel.getRangeAt(0);
    if (range.collapsed) {
      alert("Спочатку виділіть текст, якому змінити колір.");
      return;
    }
    const cssProp = prop === "backgroundColor" ? "background-color" : "color";
    const span = document.createElement("span");
    span.style.setProperty(cssProp, value);
    try {
      const frag = range.extractContents();
      span.appendChild(frag);
      span.querySelectorAll("*").forEach((el) => {
        // Прибираємо старі значення надійно
        el.style.removeProperty("color");
        el.style.removeProperty("background-color");
        el.style.removeProperty("background");
        el.removeAttribute("color");
        // Форсуємо новий колір, щоб перемогти CSS посилань (.richedit a { color:#e8402a })
        el.style.setProperty(cssProp, value);
      });
      // <font color="..."> без style — розгортаємо атрибут
      span.querySelectorAll("font").forEach((f) => {
        f.removeAttribute("color");
        f.removeAttribute("size");
        f.style.setProperty(cssProp, value);
      });
      range.insertNode(span);
      // Зливаємо порожні обгортки поруч (не критично)
      const nr = document.createRange();
      nr.selectNodeContents(span);
      sel.removeAllRanges();
      sel.addRange(nr);
      saved.current = nr.cloneRange();
    } catch (err) {
      // Фолбек на нативну команду браузера
      try {
        restoreSel();
        if (prop === "color") document.execCommand("foreColor", false, value);
        else {
          if (!document.execCommand("hiliteColor", false, value))
            document.execCommand("backColor", false, value);
        }
      } catch {}
    }
    sync();
  }

  // Обгортаємо фото у span з resize:horizontal — кутик міняє ширину; висота йде від картинки (не зникає).
  // contenteditable=false — щоб Enter/текст не йшли «всередину» фото (інакше дивні вікна/символи).
  const WRAP_STYLE = "display:inline-block;overflow:hidden;resize:horizontal;max-width:100%;min-width:120px;width:320px;height:auto;border-radius:8px;margin:8px 0;line-height:0;vertical-align:bottom";
  function hardenImgs(root) {
    (root || ref.current)?.querySelectorAll?.("img").forEach((img) => {
      img.setAttribute("referrerpolicy", "no-referrer");
      img.setAttribute("draggable", "false");
      img.style.width = "100%";
      img.style.height = "auto";
      img.style.display = "block";
      img.style.maxWidth = "100%";
    });
  }
  function prepMediaWrap(el) {
    if (!el) return;
    el.setAttribute("contenteditable", "false");
    el.setAttribute("draggable", "true");
    el.setAttribute("title", "Перетягніть, щоб перемістити. Правий нижній кутик — змінити розмір.");
  }
  function ensureCaretAfterMedia(wrap) {
    if (!wrap || !ref.current) return;
    let next = wrap.nextElementSibling;
    if (!next || !/^P$/i.test(next.tagName)) {
      next = document.createElement("p");
      next.innerHTML = "<br>";
      wrap.after(next);
    }
    try {
      const r = document.createRange();
      r.setStart(next, 0);
      r.collapse(true);
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(r);
      saved.current = r.cloneRange();
      bookmark.current = rangeToBookmark(r, ref.current);
    } catch {}
  }
  /** Прибрати білий фон у вкладених елементах callout (усі кольори: ❗⚠️⛔💡). */
  function scrubCalloutChrome(callout) {
    if (!callout) return;
    try {
      callout.querySelectorAll("*").forEach((el) => {
        el.style.removeProperty("background");
        el.style.removeProperty("background-color");
        el.style.removeProperty("background-image");
      });
    } catch {}
  }
  function scrubAllCallouts() {
    try {
      ref.current?.querySelectorAll?.(".callout").forEach(scrubCalloutChrome);
    } catch {}
  }

  /** Курсор у порожній <p> одразу після callout (вийти з блоку). */
  function ensureCaretAfterCallout(callout) {
    if (!callout || !ref.current) return;
    scrubCalloutChrome(callout);
    let next = callout.nextElementSibling;
    if (!next || !/^P$/i.test(next.tagName) || !isVisuallyEmptyBlock(next)) {
      next = document.createElement("p");
      next.innerHTML = "<br>";
      callout.after(next);
    }
    placeCaretIn(next);
  }
  /** Курсор УСЕРЕДИНІ callout (після вставки ❗/⚠️/⛔/💡) — виділити «текст…». */
  function ensureCaretInCallout(callout) {
    if (!callout || !ref.current) return;
    scrubCalloutChrome(callout);
    try {
      const span = ensureCalloutBody(callout);
      const s = window.getSelection();
      const r = document.createRange();
      if (span) {
        const ph = (span.textContent || "").replace(/[\u200b\s]/g, "").trim();
        if (ph === "текст…" || ph === "текст...") r.selectNodeContents(span);
        else { r.selectNodeContents(span); r.collapse(false); }
      } else {
        r.selectNodeContents(callout);
        r.collapse(false);
      }
      s.removeAllRanges();
      s.addRange(r);
      saved.current = r.cloneRange();
      bookmark.current = rangeToBookmark(r, ref.current);
      lastCalloutRef.current = callout;
    } catch {
      placeCaretIn(callout);
    }
  }
  function clampImgWrap(wrap) {
    if (!wrap || !wrap.classList?.contains("imgwrap")) return;
    let w = wrap.offsetWidth || parseInt(wrap.style.width, 10) || 320;
    w = Math.max(120, Math.min(w, (ref.current?.clientWidth || 800)));
    wrap.style.width = w + "px";
    wrap.style.height = "auto";
    const img = wrap.querySelector("img");
    if (img) {
      img.style.width = "100%";
      img.style.height = "auto";
    }
  }
  function wrapImages() {
    if (!ref.current) return;
    hardenImgs(ref.current);
    ref.current.querySelectorAll("img").forEach((img) => {
      const p = img.parentElement;
      if (p && p.classList && p.classList.contains("imgwrap")) {
        prepMediaWrap(p);
        clampImgWrap(p);
        return;
      }
      const span = document.createElement("span");
      span.className = "imgwrap";
      const iw = img.naturalWidth || img.width || 320;
      span.setAttribute("style", WRAP_STYLE + `;width:${Math.min(Math.max(iw, 120), 640)}px`);
      img.replaceWith(span);
      img.setAttribute("style", "width:100%;height:auto;display:block;max-width:100%");
      img.setAttribute("draggable", "false");
      span.appendChild(img);
      prepMediaWrap(span);
    });
    ref.current.querySelectorAll(".videoblock").forEach(prepMediaWrap);
  }
  // Медіа-блоки: перетягнути = перемістити; кутик = resize
  function markDraggable() {
    if (!ref.current) return;
    ref.current.querySelectorAll(".imgwrap, .videoblock").forEach(prepMediaWrap);
    ref.current.querySelectorAll("a[href]").forEach((a) => {
      if ((a.textContent || "").trim().startsWith("📎")) a.setAttribute("draggable", "true");
    });
  }
  function draggableTarget(t) {
    if (!t || !t.closest) return null;
    const media = t.closest(".imgwrap, .videoblock");
    if (media) return media;
    const a = t.closest("a[href]");
    if (a && (a.textContent || "").trim().startsWith("📎")) return a;
    return null;
  }
  function rangeFromPoint(x, y) {
    try {
      if (document.caretRangeFromPoint) return document.caretRangeFromPoint(x, y);
      if (document.caretPositionFromPoint) {
        const p = document.caretPositionFromPoint(x, y);
        if (!p) return null;
        const rr = document.createRange();
        rr.setStart(p.offsetNode, p.offset);
        rr.collapse(true);
        return rr;
      }
    } catch {}
    return null;
  }
  function clearDropCaret() {
    if (!ref.current) return;
    ref.current.querySelectorAll(".richedit-drop-caret").forEach((el) => el.remove());
  }
  /** Видимий курсор місця вставки під час drag фото/файлу */
  function placeDropCaret(clientX, clientY) {
    if (!ref.current) return;
    const r = rangeFromPoint(clientX, clientY);
    if (!r || !ref.current.contains(r.commonAncestorContainer)) {
      clearDropCaret();
      return;
    }
    if (dragEl.current && dragEl.current.contains(r.startContainer)) {
      clearDropCaret();
      return;
    }
    clearDropCaret();
    const caret = document.createElement("span");
    caret.className = "richedit-drop-caret";
    caret.contentEditable = "false";
    caret.setAttribute("aria-hidden", "true");

    const host = r.startContainer.nodeType === 3
      ? r.startContainer.parentElement : r.startContainer;
    const other = host?.closest?.(".imgwrap, .videoblock");
    if (other && other !== dragEl.current) {
      caret.classList.add("is-block");
      const mid = other.getBoundingClientRect().top + other.getBoundingClientRect().height / 2;
      if (clientY < mid) other.before(caret);
      else other.after(caret);
      return;
    }
    try {
      r.insertNode(caret);
    } catch {
      try { ref.current.appendChild(caret); } catch {}
    }
  }
  const onDragStart = (e) => {
    const t = draggableTarget(e.target);
    if (!t) { dragEl.current = null; return; }
    // кліки по video/iframe — нативні controls (перемотка), не drag блоку
    if (t.classList?.contains("videoblock") && e.target?.closest?.("video, iframe")) {
      e.preventDefault();
      dragEl.current = null;
      return;
    }
    // правий нижній кутик (~22px) — лише resize, не drag
    if (t.classList?.contains("imgwrap")) {
      const box = t.getBoundingClientRect();
      if (e.clientX > box.right - 22 && e.clientY > box.bottom - 22) {
        e.preventDefault();
        dragEl.current = null;
        return;
      }
    }
    dragEl.current = t;
    try { t.classList.add("is-dragging"); } catch {}
    try { e.dataTransfer.setData("text/plain", "move"); } catch (x) {}
    e.dataTransfer.effectAllowed = "move";
    // Замість напівпрозорого фото — маленька іконка (темні скріни інакше не видно)
    try {
      const ghost = document.createElement("div");
      ghost.className = "richedit-drag-ghost";
      const isVideo = t.classList?.contains("videoblock");
      ghost.innerHTML = isVideo
        ? "<span class='ico'>🎬</span><span>Відео</span>"
        : "<span class='ico'>🖼</span><span>Фото</span>";
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 28, 20);
      // ghost має лишатись у DOM на час старту drag
      setTimeout(() => { try { ghost.remove(); } catch {} }, 0);
    } catch {}
  };
  const onDragEnd = () => {
    try { dragEl.current?.classList?.remove("is-dragging"); } catch {}
    clearDropCaret();
    if (dropCaretRaf.current) cancelAnimationFrame(dropCaretRaf.current);
    dropCaretRaf.current = 0;
    if (dragEl.current) dragEl.current = null;
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragEl.current ? "move" : "copy";
    const x = e.clientX;
    const y = e.clientY;
    if (dropCaretRaf.current) cancelAnimationFrame(dropCaretRaf.current);
    dropCaretRaf.current = requestAnimationFrame(() => placeDropCaret(x, y));
  };
  const onDragLeave = (e) => {
    // лише коли реально виходимо з редактора
    const to = e.relatedTarget;
    if (to && ref.current?.contains(to)) return;
    clearDropCaret();
  };
  function imgHtml(src, uid) {
    const idAttr = uid ? ` data-up="${uid}"` : "";
    return `<span class="imgwrap" contenteditable="false" draggable="true" title="Перетягніть, щоб перемістити. Кутик — розмір." style="${WRAP_STYLE}"><img${idAttr} src="${String(src).replace(/"/g, "&quot;")}" referrerpolicy="no-referrer" draggable="false" style="width:100%;height:auto;display:block;max-width:100%" /></span><p><br></p>`;
  }
  async function insertImage() {
    saveSel();
    const raw = prompt("Посилання на фото (URL):");
    if (!raw) return;
    const url = raw.trim();
    if (!/^https?:\/\//i.test(url)) { alert("Потрібен повний URL (https://…)"); return; }
    // Вставляємо ОДРАЗУ (поки ще живе збережене виділення), потім підміняємо src на локальний файл
    const uid = "u" + Date.now();
    insertHTML(imgHtml(url, uid), `img[data-up="${uid}"]`);
    setUpload({ name: "фото за посиланням", pct: null, size: 0 });
    try {
      const { url: local } = await api.uploadUrl(url);
      const img = ref.current?.querySelector(`img[data-up="${uid}"]`);
      if (img) {
        img.setAttribute("src", local);
        img.removeAttribute("data-up");
        sync();
      }
    } catch (e) {
      alert("Не вдалося зберегти фото на сервер (" + (e.message || "помилка") + "). Залишено пряме посилання — якщо картинка не видно, завантажте файл і перетягніть у редактор.");
    } finally {
      setUpload(null);
    }
  }
  function isImageFile(f) {
    if (!f) return false;
    if (f.type && f.type.startsWith("image/")) return true;
    // Win/Telegram інколи віддають порожній MIME або octet-stream
    if (!f.type || f.type === "application/octet-stream") {
      return /\.(png|jpe?g|jfif|gif|webp|bmp|svg|heic|heif|avif|tiff?)$/i.test(f.name || "");
    }
    return /\.(png|jpe?g|jfif|gif|webp|bmp|svg|heic|heif|avif|tiff?)$/i.test(f.name || "");
  }
  // Drag&drop або вставка фото-файлів: завантажуємо на сервер і вставляємо в місце кидка / курсора
  async function handleFiles(files, dropRange, opts = {}) {
    const all = [...(files || [])].filter(Boolean);
    // з кнопки «🖼 Файл» довіряємо вибору користувача (accept уже обмежив)
    const imgs = opts.trustAll ? all : all.filter(isImageFile);
    const skipped = all.length - imgs.length;
    if (!imgs.length) {
      alert("Оберіть файл зображення (PNG, JPG, WebP, GIF…).");
      return;
    }
    if (dropRange) {
      saved.current = dropRange.cloneRange();
      bookmark.current = rangeToBookmark(dropRange, ref.current);
    } else if (!saved.current && !bookmark.current) {
      saveSel();
    }

    batchUpload.current = true;
    skipValueSync.current = true;
    const jobs = [];
    try {
      // 1) Усі плейсхолдери одразу в DOM (поки жива позиція курсора)
      for (let i = 0; i < imgs.length; i++) {
        const f = imgs[i];
        const uid = `u${Date.now().toString(36)}_${i}_${Math.random().toString(36).slice(2, 8)}`;
        const preview = URL.createObjectURL(f);
        insertHTML(imgHtml(preview, uid), `img[data-up="${uid}"]`);
        skipValueSync.current = true;
        try {
          const s = window.getSelection();
          if (s?.rangeCount && ref.current?.contains(s.anchorNode)) {
            saved.current = s.getRangeAt(0).cloneRange();
            bookmark.current = rangeToBookmark(saved.current, ref.current);
          }
        } catch {}
        jobs.push({ f, uid, preview });
      }
      // 2) Потім upload по черзі — DOM уже містить усі фото
      const failed = [];
      for (const job of jobs) {
        skipValueSync.current = true;
        try {
          const { url } = await doUpload(job.f);
          const img = ref.current?.querySelector(`img[data-up="${job.uid}"]`);
          if (img) {
            // спочатку новий src, blob відкликаємо лише після load — інакше скрін «блимає/зникає»
            await new Promise((resolve) => {
              let done = false;
              const finish = () => {
                if (done) return;
                done = true;
                try { URL.revokeObjectURL(job.preview); } catch {}
                resolve();
              };
              img.addEventListener("load", finish, { once: true });
              img.addEventListener("error", finish, { once: true });
              img.setAttribute("src", url);
              img.removeAttribute("data-up");
              // якщо з кешу load уже не прийде
              if (img.complete) setTimeout(finish, 0);
              setTimeout(finish, 4000);
            });
            const wrap = img.closest(".imgwrap");
            if (wrap) { prepMediaWrap(wrap); clampImgWrap(wrap); }
          } else {
            failed.push(job.f.name || "фото");
            try { URL.revokeObjectURL(job.preview); } catch {}
          }
        } catch (e) {
          const img = ref.current?.querySelector(`img[data-up="${job.uid}"]`);
          if (img) img.closest(".imgwrap")?.remove();
          failed.push(`${job.f.name || "фото"}: ${e.message || "помилка"}`);
          try { URL.revokeObjectURL(job.preview); } catch {}
        }
      }
      skipValueSync.current = true;
      sync();
      if (failed.length) {
        alert("Не всі файли додано:\n• " + failed.join("\n• "));
      } else if (skipped > 0) {
        alert(`Додано ${imgs.length} фото. Пропущено ${skipped} (не зображення).`);
      }
    } finally {
      batchUpload.current = false;
      skipValueSync.current = true;
      sync();
    }
  }
  const onDrop = (e) => {
    const caretEl = ref.current?.querySelector(".richedit-drop-caret");
    if (e.dataTransfer?.files?.length) {          // зовнішній файл → завантаження
      e.preventDefault();
      let r = null;
      if (caretEl) {
        try {
          r = document.createRange();
          r.setStartBefore(caretEl);
          r.collapse(true);
        } catch {}
      }
      if (!r) r = rangeFromPoint(e.clientX, e.clientY);
      clearDropCaret();
      handleFiles(e.dataTransfer.files, r);
      return;
    }
    if (dragEl.current) {                          // внутрішнє переміщення фото/відео
      e.preventDefault();
      e.stopPropagation();
      const moved = dragEl.current;
      try { moved.classList.remove("is-dragging"); } catch {}
      try {
        if (caretEl && caretEl.parentNode && !moved.contains(caretEl)) {
          if (caretEl.classList.contains("is-block")) {
            caretEl.replaceWith(moved);
          } else {
            caretEl.before(moved);
            caretEl.remove();
          }
          ensureCaretAfterMedia(moved);
          if (moved.classList?.contains("imgwrap")) clampImgWrap(moved);
        } else {
          const r = rangeFromPoint(e.clientX, e.clientY);
          if (r && ref.current.contains(r.commonAncestorContainer) && !moved.contains(r.startContainer)) {
            const host = r.startContainer.nodeType === 3
              ? r.startContainer.parentElement : r.startContainer;
            if (host?.closest?.(".imgwrap, .videoblock") && host.closest(".imgwrap, .videoblock") !== moved) {
              const other = host.closest(".imgwrap, .videoblock");
              const mid = other.getBoundingClientRect().top + other.getBoundingClientRect().height / 2;
              if (e.clientY < mid) other.parentNode.insertBefore(moved, other);
              else other.after(moved);
            } else {
              r.insertNode(moved);
            }
            ensureCaretAfterMedia(moved);
            if (moved.classList?.contains("imgwrap")) clampImgWrap(moved);
          }
        }
      } catch (x) {}
      clearDropCaret();
      dragEl.current = null;
      normalizeEditorDom(ref.current);
      markDraggable();
      sync();
    } else {
      clearDropCaret();
    }
  };
  /** Enter: фото/callout/нумерація. У callout Enter і Shift+Enter = новий рядок; порожній рядок + Enter = вийти. */
  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const sel = window.getSelection();
    if (!sel?.anchorNode || !ref.current || !sel.rangeCount) return;
    let n = sel.anchorNode;
    if (n.nodeType === 3) n = n.parentElement;

    const callout = n?.closest?.(".callout");
    if (callout && ref.current.contains(callout)) {
      e.preventDefault();
      const range = sel.getRangeAt(0);

      // Чи курсор на порожньому «хвості» після <br> (другий Enter підряд) → вийти з callout
      const exitIfEmptyLine = !e.shiftKey && (() => {
        try {
          // текст після останнього <br> до курсора — лише пробіли?
          const pre = document.createRange();
          pre.selectNodeContents(callout);
          pre.setEnd(range.startContainer, range.startOffset);
          const html = (() => {
            const c = pre.cloneContents();
            const d = document.createElement("div");
            d.appendChild(c);
            return d.innerHTML;
          })();
          const afterLastBr = html.split(/<br\s*\/?>/i).pop() || "";
          const plain = afterLastBr.replace(/<[^>]+>/g, "").replace(/[\u00a0\u200b\s]/g, "");
          // також: курсор одразу після <br> і далі нічого в callout
          const tail = document.createRange();
          tail.selectNodeContents(callout);
          tail.setStart(range.endContainer, range.endOffset);
          const tailPlain = (tail.toString() || "").replace(/[\u00a0\u200b\s]/g, "");
          return !plain && !tailPlain;
        } catch {
          return false;
        }
      })();

      if (exitIfEmptyLine) {
        scrubCalloutChrome(callout);
        ensureCaretAfterCallout(callout);
        sync();
        return;
      }

      // Enter / Shift+Enter — новий рядок УСЕРЕДИНІ callout
      try {
        range.deleteContents();
        const br = document.createElement("br");
        // нульова ширина після br — щоб Chrome не створював div з білим фоном
        const z = document.createTextNode("\u200b");
        range.insertNode(z);
        range.insertNode(br);
        range.setStart(z, 1);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        saveSel();
      } catch {
        try { document.execCommand("insertHTML", false, "<br>\u200b"); } catch {}
      }
      scrubCalloutChrome(callout);
      sync();
      return;
    }

    if (e.shiftKey) return; // звичайний Shift+Enter поза callout — браузер

    const wrap = n?.closest?.(".imgwrap, .videoblock");
    if (wrap && ref.current.contains(wrap)) {
      e.preventDefault();
      ensureCaretAfterMedia(wrap);
      try {
        const p = document.createElement("p");
        p.innerHTML = "<br>";
        const slot = wrap.nextElementSibling;
        if (slot) slot.after(p);
        else wrap.after(p);
        const r = document.createRange();
        r.setStart(p, 0);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
      } catch {}
      sync();
      return;
    }

    const block = n?.closest?.("p, div, li, h1, h2, h3, h4, blockquote");
    if (!block || block === ref.current || !ref.current.contains(block)) return;
    if (block.classList?.contains("imgwrap") || block.classList?.contains("videoblock")) return;
    if (block.classList?.contains("callout")) return;

    const range = sel.getRangeAt(0);
    if (!range.collapsed) return;

    // Курсор безпосередньо перед <br> (кінець пункту 1) у спільному <p>) → перейти на наступний рядок, без порожнього
    const beforeBr = (() => {
      let node = range.endContainer;
      let off = range.endOffset;
      if (node.nodeType === 3) {
        const rest = (node.nodeValue || "").slice(off).replace(/[\u00a0\u200b]/g, "").trim();
        if (rest) return null;
        let sib = node.nextSibling;
        while (sib && sib.nodeType === 3 && !String(sib.nodeValue || "").replace(/[\u00a0\u200b]/g, "").trim()) {
          sib = sib.nextSibling;
        }
        if (sib?.nodeName === "BR") return sib;
        // кінець текстового вузла всередині <b> тощо — дивимось далі по предках в межах block
        let el = node.parentNode;
        while (el && el !== block) {
          let s = el.nextSibling;
          while (s && s.nodeType === 3 && !String(s.nodeValue || "").replace(/[\u00a0\u200b]/g, "").trim()) {
            s = s.nextSibling;
          }
          if (s?.nodeName === "BR") return s;
          if (s) return null;
          el = el.parentNode;
        }
        return null;
      }
      if (node.nodeType === 1) {
        const child = node.childNodes[off];
        if (child?.nodeName === "BR") return child;
      }
      return null;
    })();
    if (beforeBr) {
      e.preventDefault();
      try {
        const r = document.createRange();
        const after = beforeBr.nextSibling;
        if (after?.nodeType === 3) r.setStart(after, 0);
        else if (after) r.setStartBefore(after);
        else r.setStartAfter(beforeBr);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
        saveSel();
      } catch {}
      return;
    }

    // Кінець блоку + нумерований пункт: не створювати порожній <p> між 1) і 2)
    const atEnd = (() => {
      try {
        const probe = document.createRange();
        probe.selectNodeContents(block);
        probe.setStart(range.endContainer, range.endOffset);
        const tail = (probe.toString() || "").replace(/[\u00a0\u200b\s]/g, "");
        return !tail;
      } catch {
        return false;
      }
    })();
    if (!atEnd) return;

    const lineText = (block.innerText || "").trim().split(/\n/).filter(Boolean).pop() || "";
    const numbered = /^\d{1,2}[.)]\s?\S/.test(lineText);
    let next = block.nextElementSibling;
    const nextEmpty = next && isVisuallyEmptyBlock(next)
      && !next.classList?.contains("imgwrap")
      && !next.classList?.contains("videoblock");
    const nextNumbered = next && !nextEmpty
      && /^\d{1,2}[.)]\s?\S/.test((next.innerText || "").trim());

    if (!numbered && !nextEmpty && !nextNumbered) return;

    e.preventDefault();
    while (next && isVisuallyEmptyBlock(next)
      && !next.classList?.contains("imgwrap")
      && !next.classList?.contains("videoblock")) {
      const rem = next;
      next = next.nextElementSibling;
      rem.remove();
    }
    if (next && ref.current.contains(next) && !isVisuallyEmptyBlock(next)) {
      try {
        const r = document.createRange();
        r.selectNodeContents(next);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
        saveSel();
      } catch {}
      sync();
      return;
    }
    // Немає наступного контенту — новий абзац з наступним номером
    const m = lineText.match(/^(\d{1,2})([.)])/);
    const p = document.createElement("p");
    if (m) {
      p.textContent = `${parseInt(m[1], 10) + 1}${m[2]} `;
    } else {
      p.innerHTML = "<br>";
    }
    block.after(p);
    placeCaretIn(p);
    sync();
  };
  /** Ctrl+C / Ctrl+X — HTML з картинками й форматуванням (imgwrap/браузер інакше часто лишає лише plain). */
  const onCopyOrCut = (e) => {
    if (!ref.current || !e.clipboardData) return;
    const html = selectionClipboardHtml(ref.current);
    if (!html) return;
    const need = selectionHasMedia(ref.current) || htmlHasRichFormat(html) || htmlHasCallout(html)
      || /<img[\s>]|<video[\s>]|<iframe[\s>]/i.test(html);
    if (!need) return;
    e.preventDefault();
    e.clipboardData.setData("text/html", `<!--StartFragment-->${html}<!--EndFragment-->`);
    e.clipboardData.setData("text/plain", htmlToPlainText(html));
    if (e.type === "cut") {
      try {
        const sel = window.getSelection();
        if (sel && sel.rangeCount) {
          sel.getRangeAt(0).deleteContents();
          wrapImages();
          sync();
        }
      } catch { /* ignore */ }
    }
  };
  const onPaste = (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    let files = [...(cd.files || [])];
    // Win/Chrome інколи кладе скрін у items, а files порожній
    if (!files.some(isImageFile) && cd.items?.length) {
      files = [...cd.items]
        .filter((it) => it.kind === "file" && (it.type?.startsWith("image/") || !it.type))
        .map((it) => it.getAsFile())
        .filter(isImageFile);
    }
    if (files.some(isImageFile)) {
      e.preventDefault();
      saveSel(); // курсор у місці paste — до втрати фокусу під час upload
      handleFiles(files.filter(isImageFile), null);
      return;
    }
    const html = cd.getData("text/html");
    // нормалізуємо \r і роздільники рядків ДО підрахунку/вставки
    let plain = collapseDuplicatedPlain(
      String(cd.getData("text/plain") || "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .replace(/[\u2028\u2029]/g, "\n")
    );
    if (!html && !plain) return;

    // Курсор ДО preventDefault — range клонуємо ДО будь-якого focus/restoreSel
    const { callout: pasteCallout, range: pasteRange } = resolvePasteCallout();

    e.preventDefault();

    // Ctrl+V у callout — plain Text+<br> у .callout-body, БЕЗ restoreSel/focus
    if (pasteCallout) {
      const text = (plain || htmlToPlainText(html || "")).replace(/\u200b/g, "");
      if (text.trim() || text.includes("\n")) {
        pasteIntoCallout(pasteCallout, text, pasteRange);
        scrubCalloutChrome(pasteCallout);
      }
      sync();
      return;
    }

    saveSel();

    const htmlHasMedia = !!(html && /<img[\s>]|<video[\s>]|<iframe[\s>]/i.test(html));
    const htmlHasFormat = htmlHasRichFormat(html);
    const htmlCallout = htmlHasCallout(html);
    const plainLines = countNonEmptyLines(plain);
    const plainNums = countNumberedLines(plain);
    // Нумеровані списки з Word/Telegram — text/plain (інакше зліпаються).
    // Курсив/жирний/картинки/callout — HTML (інакше зникають кольори блоків).
    const preferPlain = !htmlHasMedia && !htmlCallout && !!plain.trim() && (
      plainNums >= 1
      || (
        !htmlHasFormat
        && (plainLines >= 2 || !html || !/<[a-z][\s\S]*>/i.test(html || ""))
      )
    );

    let insert = "";
    if (preferPlain) {
      insert = plainTextToHtml(plain);
    } else if (html && /<[a-z][\s\S]*>/i.test(html)) {
      let raw = html;
      const frag = html.match(/<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/i);
      if (frag) raw = frag[1];
      else {
        const body = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        if (body) raw = body[1];
      }
      insert = sanitizePastedHtml(raw);
      // Дубль plain↔html — лише якщо немає медіа/форматування/callout
      if (!htmlHasMedia && !htmlHasFormat && !htmlCallout) {
        const htmlPlainNorm = collapseDuplicatedPlain(htmlToPlainText(insert).replace(/\s+/g, " ").trim());
        const plainNorm = plain.replace(/\s+/g, " ").trim();
        if (plainNorm && htmlPlainNorm && (
          htmlPlainNorm === plainNorm + plainNorm
          || (htmlPlainNorm.length >= plainNorm.length * 1.8 && htmlPlainNorm.startsWith(plainNorm) && htmlPlainNorm.endsWith(plainNorm))
        )) {
          insert = plainTextToHtml(plain);
        }
      }
    } else {
      insert = plainTextToHtml(plain);
    }
    if (!insert) return;
    pasteBlocksHtml(insert);
    try {
      normalizeEditorDom(ref.current);
      explodeNumberedListsToParagraphs(ref.current);
      compactEditorSpacing(true);
      wrapImages();
      ref.current?.querySelectorAll(".imgwrap").forEach(clampImgWrap);
      restoreCalloutStyles(ref.current);
      scrubAllCallouts();
    } catch { /* ignore */ }
    sync();
  };
  const LINK_STYLE = "color:#e8402a;text-decoration:underline;font-weight:600";
  function currentAnchor() {
    const s = window.getSelection();
    let n = s && s.anchorNode;
    while (n && n !== ref.current) { if (n.tagName === "A") return n; n = n.parentNode; }
    return null;
  }
  function insertLink() {
    saveSel();
    const a = currentAnchor();
    if (a) {   // курсор у наявному посиланні → редагувати / видалити
      const url = prompt("Посилання (URL). Лишіть порожнім, щоб ВИДАЛИТИ посилання:", a.getAttribute("href") || "");
      if (url === null) return;
      if (!url.trim()) {                       // розлінкувати (лишаємо текст)
        const p = a.parentNode;
        while (a.firstChild) p.insertBefore(a.firstChild, a);
        p.removeChild(a); sync(); return;
      }
      const href = safeUrl(url.trim());
      if (!href) { alert("Дозволені лише http(s) або /api/lms/media/… посилання."); return; }
      const text = prompt("Текст посилання:", a.textContent || href) || href;
      a.setAttribute("href", href); a.textContent = text;
      a.target = "_blank"; a.rel = "noopener noreferrer"; a.setAttribute("style", LINK_STYLE);
      sync(); return;
    }
    const url = prompt("Посилання (URL):");
    if (!url) return;
    const href = safeUrl(url.trim());
    if (!href) { alert("Дозволені лише http(s) або /api/lms/media/… посилання."); return; }
    const sel = window.getSelection();
    const hasText = sel && !sel.isCollapsed;
    restoreSel();
    if (hasText) {
      document.execCommand("createLink", false, href);
      // знайти створене посилання біля виділення, не через крихкий querySelector(href)
      try {
        const s = window.getSelection();
        let n = s?.anchorNode;
        while (n && n !== ref.current) {
          if (n.tagName === "A") {
            n.setAttribute("href", href);
            n.target = "_blank";
            n.rel = "noopener noreferrer";
            n.setAttribute("style", LINK_STYLE);
            break;
          }
          n = n.parentNode;
        }
      } catch { /* ignore */ }
      sync();
    } else {
      const text = prompt("Текст посилання:", href) || href;
      insertHTML(`<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="${LINK_STYLE}">${escapeHtml(text)}</a>&nbsp;`);
    }
  }
  // ---- Відео (файл / посилання / YouTube / Vimeo) ----
  function videoEmbed(url) {
    const yt = url.match(/(?:youtu\.be\/|[?&]v=|embed\/)([\w-]{11})/);
    if (yt) return `<iframe width="560" height="315" src="https://www.youtube.com/embed/${yt[1]}" frameborder="0" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" allowfullscreen style="max-width:100%;border-radius:8px"></iframe>`;
    const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return `<iframe src="https://player.vimeo.com/video/${vm[1]}" width="560" height="315" frameborder="0" allow="autoplay;fullscreen;picture-in-picture" allowfullscreen style="max-width:100%;border-radius:8px"></iframe>`;
    const src = String(url).replace(/"/g, "&quot;");
    // Розмір/aspect — у CSS (.videoblock video), щоб 9:16 не роздувало сторінку
    return `<video controls playsinline preload="metadata" src="${src}"></video>`;
  }
  /** Відносний /api/lms/media/… — щоб не ламався http(s)/host після проксі. */
  function mediaPlayUrl(url) {
    const u = String(url || "").trim();
    const m = u.match(/^(?:https?:\/\/[^/]+)?(\/api\/lms\/media\/[^?#]+)/i);
    if (m) return m[1];
    return safeUrl(u);
  }
  function insertVideoHTML(url, caption) {
    const safe = mediaPlayUrl(url) || safeUrl(url);
    if (!safe) { alert("Дозволені лише http(s) посилання на відео або файл з сервера."); return; }
    const uid = `v${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const cap = caption
      ? `<div style="font-weight:600;margin:4px 0">▶️ Відео: ${escapeHtml(caption)}</div>`
      : "";
    // DOM-вставка: після prompt/file dialog execCommand часто не вставляє video/iframe
    insertBlockAtCaret(
      `<div class="videoblock" data-vid="${uid}" draggable="true" contenteditable="false" style="margin:10px 0">${cap}${videoEmbed(safe)}</div><p><br></p>`,
      `.videoblock[data-vid="${uid}"]`,
    );
  }
  function insertVideo() {
    saveSel();
    const url = prompt("Посилання на відео (YouTube / Vimeo / пряме .mp4). Порожньо = завантажити файл:");
    if (url === null) return;
    if (!url.trim()) { videoFileRef.current?.click(); return; }
    const caption = prompt("Підпис до відео (необов'язково):") || "";
    insertVideoHTML(url.trim(), caption);
  }
  async function onVideoFile(e) {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const caption = prompt("Підпис до відео (необов'язково):") || "";
    try {
      const { url } = await doUpload(f);
      insertVideoHTML(url, caption);
    } catch (err) {
      alert("Не вдалося завантажити відео: " + (err.message || ""));
    }
  }
  function isVideoFile(f) {
    if (!f) return false;
    if (f.type && f.type.startsWith("video/")) return true;
    if (!f.type || f.type === "application/octet-stream") {
      return /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(f.name || "");
    }
    return /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(f.name || "");
  }
  // ---- Файл: відео → плеєр, фото → картинка, інше → посилання ----
  async function processAttachedFiles(list) {
    const files = [...(list || [])].filter(Boolean);
    if (!files.length) return;
    const imgs = files.filter(isImageFile);
    const vids = files.filter((f) => isVideoFile(f) && !isImageFile(f));
    const other = files.filter((f) => !isImageFile(f) && !isVideoFile(f));

    if (imgs.length) {
      await handleFiles(imgs, null, { trustAll: true });
    }

    batchUpload.current = true;
    skipValueSync.current = true;
    const failed = [];
    try {
      for (const f of vids) {
        skipValueSync.current = true;
        try {
          const { url } = await doUpload(f);
          skipValueSync.current = true;
          insertVideoHTML(url, f.name || "");
        } catch (err) {
          failed.push(`${f.name}: ${err.message || "помилка"}`);
        }
      }
      for (const f of other) {
        const name = String(f.name).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
        skipValueSync.current = true;
        try {
          const { url } = await doUpload(f);
          const play = mediaPlayUrl(url) || url;
          skipValueSync.current = true;
          insertHTML(`<a href="${String(play).replace(/"/g, "&quot;")}" target="_blank" rel="noopener" download="${name.replace(/"/g, "")}" draggable="true" style="${LINK_STYLE}">📎 ${name}</a>&nbsp;`);
        } catch (err) {
          failed.push(`${f.name}: ${err.message || "помилка"}`);
        }
      }
      skipValueSync.current = true;
      sync();
      if (failed.length) alert("Не всі файли додано:\n• " + failed.join("\n• "));
    } finally {
      batchUpload.current = false;
      skipValueSync.current = true;
      sync();
    }
  }
  async function insertFile() {
    saveSel();
    // Chrome/Edge: File System Access API — без фільтра розширень (видно .ahk, .untrusted тощо)
    if (typeof window.showOpenFilePicker === "function") {
      try {
        const handles = await window.showOpenFilePicker({
          multiple: true,
          excludeAcceptAllOption: false,
          // без types → у діалозі «All files»
        });
        const files = await Promise.all(handles.map((h) => h.getFile()));
        await processAttachedFiles(files);
        return;
      } catch (err) {
        if (err && (err.name === "AbortError" || err.name === "Abort")) return;
        // fallback нижче
      }
    }
    // fallback: <input> без accept
    if (fileRef.current) {
      fileRef.current.value = "";
      fileRef.current.removeAttribute("accept");
      fileRef.current.click();
    }
  }
  async function onFilePick(e) {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    await processAttachedFiles(list);
  }

  /**
   * Коллаут:
   * — є виділення → у коллаут потрапляє ЛИШЕ вибраний фрагмент (абзац розрізається);
   * — курсор у порожньому рядку → порожній коллаут там;
   * — курсор у тексті без виділення → порожній коллаут у місці курсора (абзац не забирається цілком);
   * — уже всередині callout → лише змінює тип/колір.
   */
  function insertCallout(label, bg, border, icon) {
    const ed = ref.current;
    if (!ed) return;
    saveSel();
    restoreSel();

    const uid = `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const style = `background:${bg};border-left:4px solid ${border};border-radius:8px;padding:10px 12px;`;

    const fragHtml = (frag) => {
      const tmp = document.createElement("div");
      try { tmp.appendChild(frag); } catch { return ""; }
      return tmp.innerHTML || "";
    };
    const meaningful = (html) => !!(html || "")
      .replace(/<br\s*\/?>/gi, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/[\u00a0\u200b\u200c\u200d\ufeff\s]+/g, "")
      .trim();
    /** cloneContents інколи обгортає в <p> — для тіла коллауту це зайве. */
    const unwrapBody = (html) => {
      const t = document.createElement("div");
      t.innerHTML = html || "";
      if (t.children.length === 1 && /^P$/i.test(t.children[0].tagName)) {
        return t.children[0].innerHTML;
      }
      return html || "";
    };
    /** Найближчий абзац/заголовок/li — не top-level обгортка курсу. */
    const nearestBlock = (node) => {
      let el = node;
      if (el && el.nodeType === 3) el = el.parentElement;
      while (el && el !== ed) {
        if (el.classList?.contains("callout")) return null;
        if (el.classList?.contains("imgwrap") || el.classList?.contains("videoblock")) return null;
        const tag = (el.tagName || "").toLowerCase();
        if (tag === "table" || tag === "td" || tag === "th") return null;
        if (/^(p|h1|h2|h3|h4|li|blockquote)$/.test(tag)) return el;
        el = el.parentElement;
      }
      return null;
    };

    const build = (bodyHtml) => {
      const div = document.createElement("div");
      div.className = "callout";
      div.setAttribute("data-cid", uid);
      div.setAttribute("style", style);
      const b = document.createElement("b");
      b.textContent = `${icon} ${label}:`;
      const span = document.createElement("span");
      span.className = "callout-body";
      const body = sanitizeEditorHtml(unwrapBody(bodyHtml));
      if (meaningful(body)) span.innerHTML = body;
      else span.textContent = "текст…";
      div.appendChild(b);
      div.appendChild(document.createTextNode(" "));
      div.appendChild(span);
      return div;
    };

    const replaceBlockWithParts = (block, parts) => {
      let last = null;
      parts.forEach((node, i) => {
        if (i === 0) {
          block.replaceWith(node);
          last = node;
        } else {
          last.after(node);
          last = node;
        }
      });
    };

    /** Розрізати один блок навколо range → [pre?] + callout + [post?]. */
    const splitBlockAround = (block, r, bodyHtml) => {
      const pre = document.createRange();
      pre.selectNodeContents(block);
      pre.setEnd(r.startContainer, r.startOffset);
      const post = document.createRange();
      post.selectNodeContents(block);
      post.setStart(r.endContainer, r.endOffset);
      const preHtml = fragHtml(pre.cloneContents());
      const postHtml = fragHtml(post.cloneContents());
      const callout = build(bodyHtml);
      const tag = ((block.tagName || "p").toLowerCase() === "li")
        ? "p"
        : ((block.tagName || "p").toLowerCase() === "div" ? "p" : (block.tagName || "P"));
      const parts = [];
      if (meaningful(preHtml)) {
        const el = document.createElement(tag);
        el.innerHTML = preHtml;
        parts.push(el);
      }
      parts.push(callout);
      if (meaningful(postHtml)) {
        const el = document.createElement(tag);
        el.innerHTML = postHtml;
        parts.push(el);
      }
      // li: якщо лишився порожній — прибрати з списку після заміни
      if (/^LI$/i.test(block.tagName)) {
        const list = block.parentElement;
        replaceBlockWithParts(block, parts);
        if (list && /^UL|OL$/i.test(list.tagName) && !list.children.length) {
          try { list.remove(); } catch {}
        }
      } else {
        replaceBlockWithParts(block, parts);
      }
      return callout;
    };

    const finish = (callout) => {
      if (!callout || !callout.isConnected) return;
      if (!callout.nextElementSibling) {
        const p = document.createElement("p");
        p.innerHTML = "<br>";
        callout.after(p);
      }
      scrubCalloutChrome(callout);
      hardenImgs(ed);
      markDraggable();
      skipValueSync.current = true;
      normalizeEditorDom(ed);
      const placed = ed.querySelector(`.callout[data-cid="${uid}"]`) || callout;
      if (placed?.isConnected) ensureCaretInCallout(placed);
      try { placed?.scrollIntoView?.({ block: "nearest", behavior: "smooth" }); } catch {}
      sync();
    };

    try {
      skipValueSync.current = true;
      const sel = window.getSelection();
      let range = null;
      if (sel?.rangeCount && ed.contains(sel.anchorNode)) {
        range = sel.getRangeAt(0).cloneRange();
      } else {
        range = bookmarkToRange(bookmark.current, ed);
        if (range) {
          try {
            sel.removeAllRanges();
            sel.addRange(range);
          } catch {}
        } else if (saved.current) {
          try {
            range = saved.current.cloneRange();
            sel.removeAllRanges();
            sel.addRange(range);
          } catch { range = null; }
        }
      }

      // Уже в коллауті — лише змінити тип, не плодити новий
      let host = null;
      try {
        const n = range?.commonAncestorContainer;
        const el = n?.nodeType === 1 ? n : n?.parentElement;
        host = el?.closest?.(".callout") || null;
        if (host && !ed.contains(host)) host = null;
      } catch { host = null; }
      if (host) {
        host.setAttribute("style", style);
        host.setAttribute("data-cid", uid);
        const bold = host.querySelector(":scope > b") || host.querySelector("b");
        if (bold) bold.textContent = `${icon} ${label}:`;
        let span = host.querySelector(":scope > span");
        if (!span) {
          span = document.createElement("span");
          const nodes = [...host.childNodes];
          let afterBold = false;
          nodes.forEach((ch) => {
            if (ch === bold) { afterBold = true; return; }
            if (afterBold) span.appendChild(ch);
          });
          if (!meaningful(span.innerHTML)) span.textContent = "текст…";
          host.appendChild(document.createTextNode(" "));
          host.appendChild(span);
        }
        finish(host);
        return;
      }

      // ---- Виділення: ТІЛЬКИ вибраний текст ----
      if (range && !range.collapsed && meaningful(range.toString())) {
        const midHtml = unwrapBody(fragHtml(range.cloneContents()));
        const bStart = nearestBlock(range.startContainer);
        const bEnd = nearestBlock(range.endContainer);
        let callout = null;
        if (bStart && bStart === bEnd
            && (bStart === range.startContainer || bStart.contains(range.startContainer))
            && (bStart === range.endContainer || bStart.contains(range.endContainer))) {
          callout = splitBlockAround(bStart, range, midHtml);
        } else {
          // Кілька абзаців / складний range — виймаємо лише виділене
          try {
            range.extractContents();
          } catch {
            try { range.deleteContents(); } catch {}
          }
          callout = build(midHtml);
          try {
            range.insertNode(callout);
          } catch {
            const anchor = bStart || topLevelFromNode(range.startContainer);
            if (anchor && ed.contains(anchor)) anchor.after(callout);
            else ed.appendChild(callout);
          }
          // Прибрати порожні «хвости» абзаців після extract
          [bStart, bEnd].forEach((b) => {
            if (b && b.isConnected && isVisuallyEmptyBlock(b)
                && !b.classList?.contains("callout")) {
              try { b.remove(); } catch {}
            }
          });
        }
        finish(callout);
        return;
      }

      // ---- Курсор без виділення ----
      const block = range ? nearestBlock(range.startContainer) : null;
      if (block && isVisuallyEmptyBlock(block)) {
        const callout = build("");
        block.replaceWith(callout);
        finish(callout);
        return;
      }
      if (block && range) {
        // Порожній коллаут у місці курсора — абзац не забираємо цілком
        const callout = splitBlockAround(block, range, "");
        finish(callout);
        return;
      }

      insertBlockAtCaret(
        `<div class="callout" data-cid="${uid}" style="${style}">`
        + `<b>${icon} ${label}:</b> <span>текст…</span></div><p><br></p>`,
        `.callout[data-cid="${uid}"]`
      );
    } catch (err) {
      console.error("insertCallout", err);
      try { alert("Не вдалося вставити коллаут: " + (err?.message || err)); } catch {}
    }
  }

  // ---- Таблиці ----
  function insertTable() {
    saveSel();
    const rows = parseInt(prompt("Кількість рядків:", "3"), 10);
    const cols = parseInt(prompt("Кількість колонок:", "3"), 10);
    if (!rows || !cols || rows < 1 || cols < 1) return;
    const cell = '<td style="border:1px solid #ccc;padding:6px;vertical-align:top;word-break:break-word">&nbsp;</td>';
    const uid = `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    let html = `<table data-tid="${uid}" style="border-collapse:collapse;width:100%;table-layout:fixed;margin:10px 0"><tbody>`;
    for (let r = 0; r < rows; r++) html += "<tr>" + cell.repeat(cols) + "</tr>";
    html += "</tbody></table><p><br></p>";
    insertBlockAtCaret(html, `table[data-tid="${uid}"]`);
  }
  function tableAt() {
    restoreSel();
    const s = window.getSelection();
    let n = s && s.anchorNode;
    while (n && n !== ref.current) { if (n.tagName === "TABLE") return n; n = n.parentNode; }
    // fallback: bookmark / збережений range
    try {
      const r = saved.current || (bookmark.current ? bookmarkToRange(bookmark.current, ref.current) : null);
      n = r?.commonAncestorContainer || null;
      if (n?.nodeType === 3) n = n.parentNode;
      while (n && n !== ref.current) { if (n.tagName === "TABLE") return n; n = n.parentNode; }
    } catch { /* ignore */ }
    return null;
  }
  function addRow() {
    const t = tableAt();
    if (!t) { alert("Поставте курсор усередині таблиці"); return; }
    const cols = t.rows[0]?.cells.length || 1;
    const tr = t.insertRow(-1);
    for (let i = 0; i < cols; i++) { const td = tr.insertCell(-1); td.style.border = "1px solid #ccc"; td.style.padding = "6px"; td.innerHTML = "&nbsp;"; }
    sync();
  }
  function addCol() {
    const t = tableAt();
    if (!t) { alert("Поставте курсор усередині таблиці"); return; }
    for (const tr of t.rows) { const td = tr.insertCell(-1); td.style.border = "1px solid #ccc"; td.style.padding = "6px"; td.innerHTML = "&nbsp;"; }
    sync();
  }
  function setTableWrap(mode) {
    const t = tableAt();
    if (!t) { alert("Поставте курсор усередині таблиці"); return; }
    t.style.tableLayout = "fixed"; t.style.width = "100%";
    for (const tr of t.rows) for (const td of tr.cells) {
      if (mode === "clip") {
        td.style.whiteSpace = "nowrap"; td.style.overflow = "hidden"; td.style.textOverflow = "ellipsis"; td.style.wordBreak = "";
      } else {
        td.style.whiteSpace = "normal"; td.style.overflow = ""; td.style.textOverflow = ""; td.style.wordBreak = "break-word";
      }
    }
    sync();
  }

  const Sep = () => <span style={{ width: 1, alignSelf: "stretch", background: "var(--line)", margin: "0 2px" }} />;
  const on = (active) => active ? { background: "var(--accent)", color: "#fff", borderColor: "var(--accent)" } : undefined;
  const sizeVal = SIZES_PX.includes(st.px) ? String(st.px) : "";
  const lhVal = LINE_HEIGHTS.some((x) => x.v === st.lh) ? st.lh : "";
  const paraVal = PARA_SPACINGS.some((x) => x.v === st.para) ? st.para : "";

  return (
    <div className="richwrap">
      <div className="richtoolbar">
        <button type="button" className="btn small" style={on(st.bold)} title="Жирний" onMouseDown={btn("bold")}><b>Ж</b></button>
        <button type="button" className="btn small" style={on(st.italic)} title="Курсив" onMouseDown={btn("italic")}><i>К</i></button>
        <button type="button" className="btn small" style={on(st.underline)} title="Підкреслений" onMouseDown={btn("underline")}><u>П</u></button>
        <button type="button" className="btn small" style={on(st.strike)} title="Закреслений" onMouseDown={btn("strikeThrough")}><s>З</s></button>
        <select title="Шрифт" defaultValue="" onMouseDown={saveSel} onChange={(e) => { applySel("fontName", e.target.value); e.target.selectedIndex = 0; }}>
          <option value="" disabled>Шрифт</option>
          {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
        </select>
        <select title="Розмір, px" value={sizeVal} onMouseDown={saveSel}
                onChange={(e) => { if (e.target.value) applyFontSizePx(e.target.value); }}>
          <option value="" disabled>Розмір</option>
          {SIZES_PX.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select title="Міжрядковий інтервал (line-height). Діє на виділені абзаци або весь текст."
                value={lhVal} onMouseDown={saveSel}
                onChange={(e) => { if (e.target.value) applyLineHeight(e.target.value); }}>
          <option value="" disabled>Міжрядк.</option>
          {LINE_HEIGHTS.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
        </select>
        <select title="Відступ між абзацами. Діє на виділені абзаци або весь текст."
                value={paraVal} onMouseDown={saveSel}
                onChange={(e) => { if (e.target.value !== undefined && e.target.value !== "") applyParaSpacing(e.target.value); }}>
          <option value="" disabled>Абзац</option>
          {PARA_SPACINGS.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
        </select>
        <button type="button" className="btn small" title="Ущільнити: прибрати зайві дірки між рядками (після вставки з GPT/Word)"
                onMouseDown={compactSpacing}>⇅ ущільн.</button>
        <label className="colorlbl" title="Колір тексту"
               onMouseDown={(e) => {
                 // не preventDefault на input — інакше колірний пікер не відкривається
                 if (e.target?.tagName !== "INPUT") e.preventDefault();
                 saveSel();
               }}>🎨
          <input type="color" defaultValue="#111111"
                 onMouseDown={() => saveSel()}
                 onChange={(e) => applyColor("color", e.target.value)} />
        </label>
        <label className="colorlbl" title="Колір виділення (фон)"
               onMouseDown={(e) => {
                 if (e.target?.tagName !== "INPUT") e.preventDefault();
                 saveSel();
               }}>🖍
          <input type="color" defaultValue="#fff3cd"
                 onMouseDown={() => saveSel()}
                 onChange={(e) => applyColor("backgroundColor", e.target.value)} />
        </label>
        <Sep />
        <button type="button" className="btn small" style={on(st.block === "h1")} title="Заголовок 1 (повторний клік — звичайний текст)" onMouseDown={applyHeading("H1")}>H1</button>
        <button type="button" className="btn small" style={on(st.block === "h2")} title="Заголовок 2 (повторний клік — звичайний текст)" onMouseDown={applyHeading("H2")}>H2</button>
        <button type="button" className="btn small" style={on(st.block === "h3")} title="Заголовок 3 (повторний клік — звичайний текст)" onMouseDown={applyHeading("H3")}>H3</button>
        <Sep />
        <button type="button" className="btn small" style={on(st.left)} title="Ліворуч" onMouseDown={btn("justifyLeft")}><AlignIcon type="left" /></button>
        <button type="button" className="btn small" style={on(st.center)} title="По центру" onMouseDown={btn("justifyCenter")}><AlignIcon type="center" /></button>
        <button type="button" className="btn small" style={on(st.right)} title="Праворуч" onMouseDown={btn("justifyRight")}><AlignIcon type="right" /></button>
        <button type="button" className="btn small" style={on(st.full)} title="За шириною" onMouseDown={btn("justifyFull")}><AlignIcon type="full" /></button>
        <Sep />
        <button type="button" className="btn small" style={on(st.ul)} title="Маркований список"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); saveSel(); restoreSel(); toggleList(false); }}>• список</button>
        <button type="button" className="btn small" style={on(st.ol)} title="Нумерований список"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); saveSel(); restoreSel(); toggleList(true); }}>1. список</button>
        <button type="button" className="btn small" style={on(st.block === "blockquote")} title="Цитата (повторний клік — звичайний текст)" onMouseDown={applyHeading("BLOCKQUOTE")}>❝</button>
        <Sep />
        <button type="button" className="btn small" title="Посилання (курсор у посиланні — редагувати/видалити)" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); insertLink(); }}>🔗</button>
        <button type="button" className="btn small" title="Вставити фото за посиланням" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); insertImage(); }}>🖼 URL</button>
        <button type="button" className="btn small" title="Завантажити фото (PNG, JPG, WebP…)"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); saveSel(); }}
                onClick={(e) => { e.preventDefault(); imageFileRef.current?.click(); }}>🖼 Фото</button>
        <button type="button" className="btn small" title="Вставити відео (файл / YouTube / Vimeo / посилання)" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); insertVideo(); }}>🎬 Відео</button>
        <button type="button" className="btn small" title="Прикріпити БУДЬ-ЯКИЙ файл (pdf, doc, zip, ahk, untrusted…)"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); saveSel(); }}
                onClick={(e) => { e.preventDefault(); insertFile(); }}>📎 Файл</button>
        {CALLOUTS.map(([label, bg, border, icon]) => (
          <button key={label} type="button" className="btn small" title={`Блок «${label}»`}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); saveSel(); restoreSel(); insertCallout(label, bg, border, icon); }}>{icon}</button>
        ))}
        <Sep />
        <button type="button" className="btn small" title="Вставити таблицю" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); insertTable(); }}>▦ Таблиця</button>
        <button type="button" className="btn small" title="Додати рядок" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); saveSel(); restoreSel(); addRow(); }}>＋рядок</button>
        <button type="button" className="btn small" title="Додати колонку" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); saveSel(); restoreSel(); addCol(); }}>＋стовп</button>
        <button type="button" className="btn small" title="Переносити текст у комірках" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); saveSel(); restoreSel(); setTableWrap("wrap"); }}>↩ перенос</button>
        <button type="button" className="btn small" title="Обрізати текст у комірках" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); saveSel(); restoreSel(); setTableWrap("clip"); }}>✂ обрізати</button>
        <Sep />
        <button type="button" className="btn small" title="Прибрати форматування" onMouseDown={btn("removeFormat")}>✖ формат</button>
        <Sep />
        <span style={{ position: "relative", display: "inline-block" }}>
          <button ref={aiBtnRef} type="button" className="btn small" style={{ fontWeight: 600 }}
                  title="AI-помічник: виправити / структурувати / покроково тощо"
                  onMouseDown={openAiMenu}>✨ AI</button>
        </span>
      </div>
      {aiMenu && createPortal(
        <div
          style={{
            position: "fixed",
            top: aiMenuPos.top,
            left: aiMenuPos.left,
            zIndex: 14000,
            background: "var(--bg, #fff)",
            border: "1px solid var(--line, #e5e7ea)",
            borderRadius: 8,
            boxShadow: "0 6px 24px rgba(0,0,0,.15)",
            minWidth: 220,
            padding: 4,
          }}
          onMouseLeave={() => setAiMenu(false)}
        >
          <div className="small muted" style={{ padding: "4px 8px", color: "#6b7280", fontSize: 12 }}>
            Обробити виділене (або весь текст):
          </div>
          {AI_ACTIONS_UI.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className="btn small"
              style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: "none", padding: "6px 8px", cursor: "pointer" }}
              onMouseDown={(e) => { e.preventDefault(); runAI(key); }}
            >
              {label}
            </button>
          ))}
        </div>,
        document.body,
      )}
      {upload && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#fff7e0", borderBottom: "1px solid var(--line)" }}>
          <span className="small" style={{ whiteSpace: "nowrap" }}>⏳ Завантаження: <b>{upload.name}</b>{upload.size ? ` (${(upload.size / 1048576).toFixed(1)} МБ)` : ""}</span>
          <div style={{ flex: 1, height: 8, background: "rgba(0,0,0,.1)", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${upload.pct ?? 0}%`, height: "100%", background: "var(--accent, #e8402a)", transition: "width .2s ease" }} />
          </div>
          <span className="small" style={{ width: 42, textAlign: "right" }}>{upload.pct != null ? `${upload.pct}%` : "…"}</span>
        </div>
      )}
      <div ref={ref} className="richedit" contentEditable onInput={syncFromInput}
           onMouseUp={() => {
             wrapImages();
             ref.current?.querySelectorAll(".imgwrap").forEach(clampImgWrap);
             refresh();
           }}
           onKeyDown={onKeyDown}
           onKeyUp={refresh}
           onDrop={onDrop}
           onDragOver={onDragOver}
           onDragLeave={onDragLeave}
           onDragStart={onDragStart}
           onDragEnd={onDragEnd}
           onCopy={onCopyOrCut}
           onCut={onCopyOrCut}
           onPaste={onPaste}
           suppressContentEditableWarning
           data-ph="Вводьте текст або перетягніть фото сюди. Фото можна переміщати перетягуванням; кутик — змінити розмір." />
      <input type="file" accept="image/*,.png,.jpg,.jpeg,.jfif,.gif,.webp,.bmp,.heic,.heif,.avif" ref={imageFileRef} style={{ display: "none" }} multiple
             onChange={(e) => {
               // ВАЖЛИВО: FileList «живий» — після value="" стає порожнім. Копіюємо ДО скидання.
               const files = Array.from(e.target.files || []);
               e.target.value = "";
               if (files.length) handleFiles(files, null, { trustAll: true });
             }} />
      <input type="file" accept="video/*,.mp4,.webm,.mov,.m4v,.avi,.mkv,video/mp4,video/webm,video/quicktime" ref={videoFileRef} style={{ display: "none" }} onChange={onVideoFile} />
      {/* без accept — Windows показує «Усі файли», інакше лише png/jpeg */}
      <input type="file" ref={fileRef} style={{ display: "none" }} multiple onChange={onFilePick} />
      {ai && createPortal(
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 14100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget && !ai.busy) setAi(null); }}
        >
          <div style={{ background: "#fff", borderRadius: 12, maxWidth: 780, width: "100%", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,.3)" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7ea", display: "flex", alignItems: "center", gap: 8 }}>
              <b>✨ AI-помічник {ai.scope === "sel" ? "(виділений фрагмент)" : "(весь матеріал)"}</b>
              <button type="button" className="btn small" style={{ marginLeft: "auto" }} disabled={ai.busy} onClick={() => setAi(null)}>✕</button>
            </div>
            <div style={{ padding: 16, overflow: "auto", flex: 1 }}>
              {ai.busy ? (
                <div style={{ textAlign: "center", padding: 30 }}>
                  <div className="small muted" style={{ marginTop: 10 }}>AI обробляє текст…</div>
                </div>
              ) : ai.error ? (
                <div className="pill no" style={{ padding: "8px 12px", background: "#fdecea", color: "#b71c1c", borderRadius: 8 }}>{ai.error}</div>
              ) : (
                <div className="coursehtml" style={{ border: "1px solid #e5e7ea", borderRadius: 8, padding: 12 }} dangerouslySetInnerHTML={{ __html: sanitizeEditorHtml(ai.result || "") }} />
              )}
            </div>
            {!ai.busy && !ai.error && (
              <div style={{ padding: "12px 16px", borderTop: "1px solid #e5e7ea", display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" className="btn primary" onClick={() => applyAI("replace")}>
                  {ai.scope === "sel" ? "Замінити виділене" : "Замінити весь текст"}
                </button>
                <button type="button" className="btn" onClick={() => applyAI("insert")}>Вставити {ai.scope === "sel" ? "після виділеного" : "у курсор"}</button>
                <button type="button" className="btn" style={{ marginLeft: "auto" }} onClick={() => setAi(null)}>Скасувати</button>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
